import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  IconPlus, IconSearch, IconX, IconUser, IconUsers,
  IconTrash, IconEdit, IconFolderPlus, IconNote, IconClock,
  IconSend, IconBrandWhatsapp, IconChartBar, IconDownload, IconUpload,
  IconCheck, IconChevronLeft, IconChevronRight, IconArrowUp, IconArrowDown,
  IconFilter, IconTag, IconArrowMerge, IconWand, IconSelector,
  IconArrowsMaximize, IconCopy, IconLink, IconCalendar, IconMessage, IconAntenna, IconDotsVertical, IconBolt,
  IconTicket, IconSpeakerphone, IconBold, IconItalic, IconAt, IconMail
} from '@tabler/icons-react';
import { AGENTS } from '../data/industries';

// ── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(nameEn, nameAr, lang) {
  const name = lang === 'ar' ? (nameAr || nameEn) : (nameEn || nameAr);
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const AVATAR_COLORS = [
  '#5B2D8E', '#1B6B93', '#B5651D', '#7B2D5F', '#DC2626',
  '#0891B2', '#D97706', '#DB2777', '#22C55E', '#6366F1'
];

function avatarColor(id) {
  const hash = (id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getContactSegments(contact, segments) {
  return segments.filter(seg =>
    Array.isArray(seg.tags) && seg.tags.length > 0 &&
    seg.tags.every(tagId => contact.tags.includes(tagId))
  );
}

function getSegmentCount(seg, contacts) {
  if (!Array.isArray(seg.tags) || seg.tags.length === 0) return 0;
  return contacts.filter(c => seg.tags.every(tagId => c.tags.includes(tagId))).length;
}

const PRESET_COLORS = ['#6B7280', '#1B6B93', '#B5651D', '#7B2D5F', '#22C55E', '#EF4444', '#D97706', '#6366F1'];

function evaluateConditions(contact, conditions, groups) {
  if (!conditions || !conditions.rules || conditions.rules.length === 0) return false;
  const now = new Date();
  const toDays = (val, unit) => {
    const n = parseInt(val, 10) || 0;
    if (unit === 'weeks') return n * 7;
    if (unit === 'months') return n * 30;
    return n;
  };
  const daysSince = (dateStr) => {
    if (!dateStr) return Infinity;
    const d = new Date(dateStr);
    return isNaN(d) ? Infinity : Math.floor((now - d) / 86400000);
  };
  const check = (rule) => {
    // Tag
    if (rule.field === 'tag') {
      if (rule.op === 'includes') return (contact.tags || []).includes(rule.value);
      if (rule.op === 'doesNotInclude') return !(contact.tags || []).includes(rule.value);
    }
    // Group
    if (rule.field === 'group') {
      const g = (groups || []).find(gr => gr.id === rule.value);
      const isMember = g ? (g.members || []).includes(contact.id) : false;
      if (rule.op === 'memberOf') return isMember;
      if (rule.op === 'notMemberOf') return !isMember;
    }
    // Phone
    if (rule.field === 'phone') {
      const phone = contact.phone || '';
      if (rule.op === 'startsWith') return phone.startsWith(rule.value || '');
      if (rule.op === 'contains') return phone.includes(rule.value || '');
    }
    // Name
    if (rule.field === 'name') {
      const name = ((contact.nameEn || '') + ' ' + (contact.nameAr || '')).toLowerCase();
      if (rule.op === 'contains') return name.includes((rule.value || '').toLowerCase());
    }
    // Last active
    if (rule.field === 'lastActive') {
      const days = daysSince(contact.lastActive);
      const threshold = toDays(rule.value, rule.unit);
      if (rule.op === 'within') return days <= threshold;
      if (rule.op === 'moreThan') return days > threshold;
    }
    // Created date
    if (rule.field === 'createdDate') {
      const days = daysSince(contact.createdAt);
      const threshold = toDays(rule.value, rule.unit);
      if (rule.op === 'within') return days <= threshold;
      if (rule.op === 'before') return days > threshold;
    }
    // Conversations
    if (rule.field === 'conversations') {
      const count = contact.conversationCount || 0;
      const val = parseInt(rule.value, 10) || 0;
      if (rule.op === 'moreThan') return count > val;
      if (rule.op === 'lessThan') return count < val;
    }
    // Channel
    if (rule.field === 'channel') {
      const ch = contact.channel || '';
      if (rule.op === 'is') return ch === rule.value;
      if (rule.op === 'isNot') return ch !== rule.value;
    }
    return false;
  };
  if (conditions.logic === 'OR') return conditions.rules.some(check);
  return conditions.rules.every(check);
}

function relativeTime(dateStr, t, isRtl) {
  if (!dateStr) return '—';
  const now = new Date();
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return t.justNow || (isRtl ? 'الآن' : 'just now');
  if (diffMin < 60) return isRtl ? `${diffMin} ${t.minAgo || 'د'}` : `${diffMin} ${t.minAgo || 'min ago'}`;
  if (diffHr < 24) return isRtl ? `${diffHr} ${t.hoursAgo || 'س'}` : `${diffHr} ${t.hoursAgo || 'hours ago'}`;
  if (diffDay === 1) return t.yesterday || (isRtl ? 'أمس' : 'Yesterday');
  return isRtl ? `${diffDay} ${t.daysAgo || 'يوم'}` : `${diffDay} ${t.daysAgo || 'days ago'}`;
}

// ── TagInput ─────────────────────────────────────────────────────────────────

function TagInput({ selectedTagIds, allTags, onChange, onCreateTag, placeholder, lang }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filtered = allTags.filter(tag =>
    !selectedTagIds.includes(tag.id) &&
    (tag.labelEn.toLowerCase().includes(query.toLowerCase()) ||
     (tag.labelAr || '').includes(query))
  );

  const exactMatch = allTags.some(
    t => t.labelEn.toLowerCase() === query.toLowerCase() ||
         (t.labelAr || '') === query
  );

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function removeTag(id) {
    onChange(selectedTagIds.filter(t => t !== id));
  }

  function addTag(id) {
    onChange([...selectedTagIds, id]);
    setQuery('');
    inputRef.current?.focus();
  }

  function handleCreate() {
    if (!query.trim()) return;
    const newTag = onCreateTag(query.trim());
    onChange([...selectedTagIds, newTag.id]);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  }

  const selectedTags = allTags.filter(t => selectedTagIds.includes(t.id));
  const isRtl = lang === 'ar';

  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        style={{
          minHeight: 40, padding: '6px 10px', borderRadius: 8,
          border: '1px solid #E5E7EB', background: '#fff',
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          cursor: 'text'
        }}
      >
        {selectedTags.map(tag => (
          <span key={tag.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
            background: tag.color + '18', color: tag.color, border: `1px solid ${tag.color}40`
          }}>
            {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
            <button
              onClick={e => { e.stopPropagation(); removeTag(tag.id); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                color: tag.color, lineHeight: 1, display: 'flex', alignItems: 'center' }}
            >
              <IconX size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          style={{
            border: 'none', outline: 'none', fontSize: 13, flex: 1,
            minWidth: 80, background: 'transparent', fontFamily: 'inherit',
            direction: isRtl ? 'rtl' : 'ltr'
          }}
        />
      </div>

      {open && (filtered.length > 0 || (query.trim() && !exactMatch)) && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
            background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden'
          }}
        >
          {filtered.slice(0, 6).map(tag => (
            <div
              key={tag.id}
              onMouseDown={e => { e.preventDefault(); addTag(tag.id); }}
              style={{
                padding: '10px 12px', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 8, fontSize: 13,
                direction: isRtl ? 'rtl' : 'ltr'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: tag.color, flexShrink: 0
              }} />
              {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
            </div>
          ))}
          {query.trim() && !exactMatch && (
            <div
              onMouseDown={e => { e.preventDefault(); handleCreate(); }}
              style={{
                padding: '10px 12px', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600,
                color: '#5B2D8E', borderTop: filtered.length > 0 ? '1px solid #F3F4F6' : 'none',
                direction: isRtl ? 'rtl' : 'ltr'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F7F2FC'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <IconPlus size={14} />
              {isRtl ? `إنشاء وسم "${query.trim()}"` : `Create tag "${query.trim()}"`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ContactSlideOver ──────────────────────────────────────────────────────────

function ContactSlideOver({ contact, allTags, segments, contacts, lang, t, onSave, onDelete, onClose, onCreateTag, onAddNote, onUpdateContact, groups, onSetGroups, industry }) {
  const isNew = !contact || contact === 'new';
  const isRtl = lang === 'ar';

  const [nameAr, setNameAr] = useState(isNew ? '' : (contact.nameAr || ''));
  const [nameEn, setNameEn] = useState(isNew ? '' : (contact.nameEn || ''));
  const [phone, setPhone] = useState(isNew ? '' : (contact.phone || ''));
  const [tagIds, setTagIds] = useState(isNew ? [] : (contact.tags || []));
  const [drawerTab, setDrawerTab] = useState('details');
  const [noteText, setNoteText] = useState('');
  const [editingNoteIdx, setEditingNoteIdx] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [noteMenuIdx, setNoteMenuIdx] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');

  const previewSegs = segments.filter(seg =>
    Array.isArray(seg.tags) && seg.tags.length > 0 &&
    seg.tags.every(id => tagIds.includes(id))
  );

  const notes = (!isNew && contact.notes) || [];
  const history = (!isNew && contact.history) || [];

  function handleSave() {
    const nameArVal = nameAr.trim();
    const nameEnVal = nameEn.trim();
    if (!nameArVal && !nameEnVal) return;
    onSave({
      ...(isNew ? { id: 'c_' + Date.now() } : contact),
      nameAr: nameArVal,
      nameEn: nameEnVal,
      phone: phone.trim(),
      tags: tagIds,
    });
  }

  function handleAddNote() {
    if (!noteText.trim() || isNew) return;
    onAddNote(contact.id, noteText.trim());
    setNoteText('');
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
    fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    direction: isRtl ? 'rtl' : 'ltr'
  };

  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block'
  };

  const sectionLabelStyle = {
    fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em',
    textTransform: 'uppercase', marginBottom: 10,
    display: 'flex', alignItems: 'center', gap: 6,
    flexDirection: isRtl ? 'row-reverse' : 'row'
  };

  // Dashboard mock stats
  const dashStats = !isNew ? {
    totalMessages: Math.floor(Math.random() * 80 + 20),
    avgResponse: (Math.random() * 4 + 1).toFixed(1),
    lastActive: isRtl ? 'منذ ساعتين' : '2 hours ago',
    satisfaction: Math.floor(Math.random() * 30 + 70),
    conversations: Math.floor(Math.random() * 10 + 2),
    resolvedTickets: Math.floor(Math.random() * 8 + 1),
  } : null;

  const contactGroups = (groups || []).filter(g => (g.members || []).includes(contact?.id));
  const smartGroupMatches = (segments || []).filter(seg => seg.type === 'smart' && seg.conditions && !isNew && evaluateConditions(contact, seg.conditions, groups));

  // Mock conversations from industry
  const contactConversations = useMemo(() => {
    if (isNew || !industry) return [];
    const phone = contact?.phone || '';
    return (industry.conversations || []).filter(c => c.phone === phone);
  }, [isNew, contact, industry]);

  // Customer since date
  const customerSinceLabel = useMemo(() => {
    if (isNew || !contact?.createdAt) return '';
    const d = new Date(contact.createdAt);
    if (isNaN(d)) return '';
    const monthsEn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthsAr = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
    const m = d.getMonth();
    const y = d.getFullYear();
    if (isRtl) return `عميل منذ ${monthsAr[m]} ${y}`;
    return `Customer since ${monthsEn[m]} ${y}`;
  }, [isNew, contact, isRtl]);

  // Customer tenure in days
  const customerTenureDays = useMemo(() => {
    if (isNew || !contact?.createdAt) return 0;
    const d = new Date(contact.createdAt);
    return isNaN(d) ? 0 : Math.floor((new Date() - d) / 86400000);
  }, [isNew, contact]);

  // Demo author names for notes
  const NOTE_AUTHORS = [
    { name: 'Thaer Abbas', nameAr: 'ثائر عباس', color: '#5B2D8E' },
    { name: 'Sara Al Jindan', nameAr: 'سارة الجندان', color: '#1B6B93' },
    { name: AGENTS.abdullah.name, nameAr: AGENTS.abdullah.nameAr, color: AGENTS.abdullah.color },
    { name: AGENTS.latifa.name, nameAr: AGENTS.latifa.nameAr, color: AGENTS.latifa.color },
    { name: AGENTS.khalid.name, nameAr: AGENTS.khalid.nameAr, color: AGENTS.khalid.color },
  ];

  // Generate activity entries from history + enriched demo data
  const activityEntries = useMemo(() => {
    if (isNew) return [];
    const entries = [];
    // Enrich history with types
    (history || []).forEach((h, i) => {
      const actionLower = (h.actionEn || '').toLowerCase();
      let type = 'conversation';
      if (actionLower.includes('tag') || actionLower.includes('وسم')) type = 'tag';
      else if (actionLower.includes('group') || actionLower.includes('مجموع')) type = 'group';
      else if (actionLower.includes('campaign') || actionLower.includes('broadcast') || actionLower.includes('حملة') || actionLower.includes('بث')) type = 'broadcast';
      else if (actionLower.includes('resolved') || actionLower.includes('حل')) type = 'resolved';
      entries.push({ ...h, type, id: 'hist_' + i });
    });
    // Add some synthetic activity if entries are sparse
    if (entries.length < 4) {
      const synthetics = [
        { type: 'conversation', actionEn: 'WhatsApp conversation started', actionAr: 'بدأت محادثة واتساب', dateEn: 'Today, 10:30 AM', dateAr: 'اليوم، ١٠:٣٠ ص', dayGroup: 'today' },
        { type: 'tag', actionEn: 'Tag "VIP" added', actionAr: 'تم إضافة وسم "VIP"', dateEn: 'Today, 9:15 AM', dateAr: 'اليوم، ٩:١٥ ص', dayGroup: 'today' },
        { type: 'group', actionEn: 'Added to "Premium Clients" group', actionAr: 'أضيف إلى مجموعة "عملاء مميزين"', dateEn: 'Mar 15', dateAr: '١٥ مارس', dayGroup: 'week' },
        { type: 'broadcast', actionEn: 'Received "Spring Offer" broadcast', actionAr: 'استقبل بث "عرض الربيع"', dateEn: 'Mar 14', dateAr: '١٤ مارس', dayGroup: 'week' },
        { type: 'resolved', actionEn: 'Ticket #1042 resolved', actionAr: 'تم حل التذكرة #1042', dateEn: 'Mar 13', dateAr: '١٣ مارس', dayGroup: 'week' },
        { type: 'conversation', actionEn: 'Follow-up call completed', actionAr: 'تم إكمال مكالمة المتابعة', dateEn: 'Mar 12', dateAr: '١٢ مارس', dayGroup: 'week' },
      ];
      synthetics.forEach((s, i) => {
        if (entries.length < 6) entries.push({ ...s, id: 'synth_' + i });
      });
    } else {
      // Assign day groups to existing
      entries.forEach((e, i) => {
        if (i < 2) e.dayGroup = 'today';
        else e.dayGroup = 'week';
      });
    }
    return entries;
  }, [isNew, history]);

  // Filtered activity entries
  const filteredActivity = useMemo(() => {
    if (activityFilter === 'all') return activityEntries;
    const typeMap = { groups: 'group', tags: 'tag', broadcasts: 'broadcast', conversations: 'conversation' };
    return activityEntries.filter(e => e.type === typeMap[activityFilter]);
  }, [activityEntries, activityFilter]);

  // Activity summary stats
  const activityStats = useMemo(() => {
    return {
      conversations: contact?.conversationCount || activityEntries.filter(e => e.type === 'conversation').length,
      tenure: customerTenureDays,
      broadcasts: activityEntries.filter(e => e.type === 'broadcast').length || 3,
      groups: (groups || []).filter(g => (g.members || []).includes(contact?.id)).length || 2,
    };
  }, [activityEntries, contact, customerTenureDays, groups]);

  // Demo conversation list entries with metadata
  const demoConversationList = useMemo(() => {
    if (isNew || !industry) return [];
    const statuses = ['active', 'resolved', 'escalated'];
    const channels = ['whatsapp', 'sms', 'email'];
    const agentNames = [
      { en: AGENTS.abdullah.name, ar: AGENTS.abdullah.nameAr },
      { en: AGENTS.latifa.name, ar: AGENTS.latifa.nameAr },
      { en: AGENTS.khalid.name, ar: AGENTS.khalid.nameAr },
    ];
    const subjectsEn = ['Product inquiry', 'Delivery follow-up', 'Pricing request', 'Support ticket #1042', 'Account update'];
    const subjectsAr = ['استفسار عن المنتج', 'متابعة التوصيل', 'طلب سعر', 'تذكرة دعم #1042', 'تحديث الحساب'];
    const previewsEn = ['Hi, I wanted to ask about...', 'Thanks for the update, when can I expect...', 'Could you send me the latest pricing...', 'The issue has been resolved, thank you...', 'Please update my account details...'];
    const previewsAr = ['مرحباً، أريد أن أسأل عن...', 'شكراً على التحديث، متى أتوقع...', 'هل يمكنك إرسال آخر الأسعار...', 'تم حل المشكلة، شكراً...', 'الرجاء تحديث بيانات حسابي...'];
    const times = ['2m ago', '1h ago', '3h ago', 'Yesterday', '2d ago'];
    const timesAr = ['منذ ٢ د', 'منذ ساعة', 'منذ ٣ س', 'أمس', 'منذ يومين'];

    // Use real conversations if available, otherwise generate
    if (contactConversations.length > 0) {
      return contactConversations.map((conv, i) => ({
        id: conv.id,
        status: statuses[i % 3],
        subjectEn: subjectsEn[i % 5],
        subjectAr: subjectsAr[i % 5],
        previewEn: (conv.messages || []).find(m => m.from !== 'system')?.en || previewsEn[i % 5],
        previewAr: (conv.messages || []).find(m => m.from !== 'system')?.ar || previewsAr[i % 5],
        channel: contact?.channel || channels[i % 3],
        agent: agentNames[i % 3],
        timeEn: times[i % 5],
        timeAr: timesAr[i % 5],
      }));
    }
    // Generate demo entries
    return Array.from({ length: 3 }, (_, i) => ({
      id: 'demo_conv_' + i,
      status: statuses[i % 3],
      subjectEn: subjectsEn[i],
      subjectAr: subjectsAr[i],
      previewEn: previewsEn[i],
      previewAr: previewsAr[i],
      channel: contact?.channel || channels[i % 3],
      agent: agentNames[i % 3],
      timeEn: times[i],
      timeAr: timesAr[i],
    }));
  }, [isNew, industry, contactConversations, contact]);

  // Note handlers
  function handleDeleteNote(idx) {
    if (!onUpdateContact || isNew) return;
    const updatedNotes = [...(contact.notes || [])];
    updatedNotes.splice(idx, 1);
    onUpdateContact(contact.id, { notes: updatedNotes });
  }
  function handleEditNoteSave(idx) {
    if (!onUpdateContact || isNew || !editingNoteText.trim()) return;
    const updatedNotes = [...(contact.notes || [])];
    updatedNotes[idx] = { ...updatedNotes[idx], textEn: editingNoteText, textAr: editingNoteText };
    onUpdateContact(contact.id, { notes: updatedNotes });
    setEditingNoteIdx(null);
    setEditingNoteText('');
  }

  const [addGroupOpen, setAddGroupOpen] = useState(false);

  const DRAWER_TABS = [
    { key: 'details', labelEn: 'Details', labelAr: 'التفاصيل', icon: IconUser },
    { key: 'notes', labelEn: 'Notes', labelAr: 'الملاحظات', icon: IconNote },
    { key: 'activity', labelEn: t.activityTab || 'Activity', labelAr: t.activityTab || 'النشاط', icon: IconClock },
    { key: 'conversations', labelEn: t.conversationsTab || 'Conversations', labelAr: t.conversationsTab || 'المحادثات', icon: IconBrandWhatsapp },
    { key: 'dashboard', labelEn: 'Dashboard', labelAr: 'لوحة المتابعة', icon: IconChartBar },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200,
          animation: 'fadeIn 0.15s ease'
        }}
      />
      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, bottom: 0,
        [isRtl ? 'left' : 'right']: 0,
        width: 440, background: '#fff', zIndex: 201,
        boxShadow: isRtl ? '4px 0 24px rgba(0,0,0,0.15)' : '-4px 0 24px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideRight 0.25s ease'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 0', borderBottom: '1px solid #E5E7EB',
          display: 'flex', flexDirection: 'column'
        }}>
          {/* Breadcrumb */}
          {!isNew && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
              fontSize: 12, flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <span
                onClick={onClose}
                style={{ color: '#5B2D8E', cursor: 'pointer', fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                {t.contactsTab || (isRtl ? 'جهات الاتصال' : 'Contacts')}
              </span>
              <span style={{ color: '#9CA3AF' }}>{isRtl ? '‹' : '›'}</span>
              <span style={{ color: '#6B7280', fontWeight: 500 }}>
                {lang === 'ar' ? (contact.nameAr || contact.nameEn) : (contact.nameEn || contact.nameAr)}
              </span>
            </div>
          )}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexDirection: isRtl ? 'row-reverse' : 'row', marginBottom: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
              flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: isNew ? '#F7F2FC' : avatarColor(contact.id),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isNew ? '#5B2D8E' : '#fff', fontWeight: 700, fontSize: 13
              }}>
                {isNew ? <IconUser size={18} /> : getInitials(contact.nameEn, contact.nameAr, lang)}
              </div>
              <div>
                <span style={{ fontWeight: 700, fontSize: 16, display: 'block' }}>
                  {isNew
                    ? (t.newContact || (isRtl ? 'عميل جديد' : 'New Contact'))
                    : (lang === 'ar' ? (contact.nameAr || contact.nameEn) : (contact.nameEn || contact.nameAr))}
                </span>
                {!isNew && contact.phone && (
                  <span style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {contact.phone}
                  </span>
                )}
                {!isNew && customerSinceLabel && (
                  <span style={{ fontSize: 11, color: '#6B7280', display: 'block', marginTop: 1 }}>
                    {customerSinceLabel}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9CA3AF', padding: 4, borderRadius: 6,
              display: 'flex', alignItems: 'center'
            }}>
              <IconX size={20} />
            </button>
          </div>

          {/* Action Buttons + Tabs */}
          {!isNew && (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button
                  onClick={() => {
                    const num = (contact.phone || '').replace(/[^0-9+]/g, '');
                    if (num) window.open(`https://wa.me/${num.replace('+', '')}`, '_blank');
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    flex: 1, padding: '9px 0', borderRadius: 8,
                    background: '#22C55E', color: '#fff', border: 'none',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    flexDirection: isRtl ? 'row-reverse' : 'row'
                  }}
                >
                  <IconBrandWhatsapp size={18} />
                  {t.startConversation || (isRtl ? 'بدء محادثة' : 'Start Conversation')}
                </button>
                <button
                  onClick={() => {/* Demo: ticket action */}}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    flex: 1, padding: '9px 0', borderRadius: 8,
                    background: '#F3F0F8', color: '#5B2D8E', border: '1px solid #D8C5F5',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    flexDirection: isRtl ? 'row-reverse' : 'row'
                  }}
                >
                  <IconTicket size={18} />
                  {t.ticket || (isRtl ? 'تذكرة' : 'Ticket')}
                </button>
              </div>

              <div style={{ display: 'flex', gap: 0 }}>
                {DRAWER_TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setDrawerTab(tab.key)}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '8px 0', border: 'none', cursor: 'pointer',
                      fontWeight: drawerTab === tab.key ? 700 : 500, fontSize: 12,
                      fontFamily: 'inherit',
                      color: drawerTab === tab.key ? '#5B2D8E' : '#9CA3AF',
                      borderBottom: drawerTab === tab.key ? '2px solid #5B2D8E' : '2px solid transparent',
                      background: 'transparent',
                      flexDirection: isRtl ? 'row-reverse' : 'row'
                    }}
                  >
                    <tab.icon size={14} />
                    {isRtl ? tab.labelAr : tab.labelEn}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

          {/* === Details Tab === */}
          {(isNew || drawerTab === 'details') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Name AR */}
              <div>
                <label style={labelStyle}>
                  {isRtl ? 'الاسم بالعربي' : 'Arabic Name'}
                </label>
                <input
                  style={inputStyle}
                  value={nameAr}
                  onChange={e => setNameAr(e.target.value)}
                  placeholder="مثال: فهد العتيبي"
                  dir="rtl"
                />
              </div>

              {/* Name EN */}
              <div>
                <label style={labelStyle}>
                  {isRtl ? 'الاسم بالإنجليزي' : 'English Name'}
                </label>
                <input
                  style={{ ...inputStyle, direction: 'ltr' }}
                  value={nameEn}
                  onChange={e => setNameEn(e.target.value)}
                  placeholder="e.g. Fahad Al-Otaibi"
                />
              </div>

              {/* Phone */}
              <div>
                <label style={labelStyle}>
                  {t.contactPhone || (isRtl ? 'الجوال' : 'Phone')}
                </label>
                <input
                  style={{ ...inputStyle, direction: 'ltr' }}
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+966 5X XXX XXXX"
                />
              </div>

              {/* Tags */}
              <div>
                <label style={labelStyle}>
                  {t.contactTags || (isRtl ? 'الوسوم' : 'Tags')}
                </label>
                <TagInput
                  selectedTagIds={tagIds}
                  allTags={allTags}
                  onChange={setTagIds}
                  onCreateTag={onCreateTag}
                  placeholder={t.addTag || (isRtl ? 'أضف وسم...' : 'Add tag...')}
                  lang={lang}
                />
              </div>

              {/* Segment preview */}
              <div style={{
                padding: '14px 16px', borderRadius: 10,
                background: previewSegs.length > 0 ? '#F7F2FC' : '#F9FAFB',
                border: `1px solid ${previewSegs.length > 0 ? '#D8C5F5' : '#E5E7EB'}`
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 8,
                  textAlign: isRtl ? 'right' : 'left'
                }}>
                  {t.willBeIn || (isRtl ? 'سيُضاف إلى شرائح:' : 'Will be in segments:')}
                </div>
                {previewSegs.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: isRtl ? 'right' : 'left' }}>
                    {t.noSegmentsMatch || (isRtl ? 'لا توجد شرائح مطابقة' : 'No matching segments')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {previewSegs.map(seg => (
                      <span key={seg.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        background: seg.color + '15', color: seg.color,
                        border: `1px solid ${seg.color}30`
                      }}>
                        {lang === 'ar' ? (seg.nameAr || seg.nameEn) : seg.nameEn}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Group Membership */}
              {!isNew && (contactGroups.length > 0 || smartGroupMatches.length > 0) && (
                <div style={{
                  padding: '14px 16px', borderRadius: 10,
                  background: '#F9FAFB', border: '1px solid #E5E7EB'
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 8, textAlign: isRtl ? 'right' : 'left' }}>
                    {t.groups || (isRtl ? 'المجموعات' : 'Groups')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {contactGroups.map(g => (
                      <span key={g.id} style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        background: g.color + '15', color: g.color, border: `1px solid ${g.color}30`
                      }}>
                        {lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}
                      </span>
                    ))}
                    {smartGroupMatches.map(sg => (
                      <span key={sg.id} style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        background: sg.color + '15', color: sg.color, border: `1px dashed ${sg.color}50`
                      }}>
                        {lang === 'ar' ? (sg.nameAr || sg.nameEn) : sg.nameEn}
                        <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4, marginRight: 4 }}>{isRtl ? '(ذكية)' : '(smart)'}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Add to Group */}
              {!isNew && groups && groups.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setAddGroupOpen(!addGroupOpen)} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                    borderRadius: 8, border: '1px dashed #D1D5DB', background: 'transparent',
                    color: '#6B7280', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                    flexDirection: isRtl ? 'row-reverse' : 'row'
                  }}>
                    <IconPlus size={14} />
                    {t.addToGroup || (isRtl ? 'أضف لمجموعة' : 'Add to Group')}
                  </button>
                  {addGroupOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, maxHeight: 200, overflowY: 'auto'
                    }}>
                      {groups.filter(g => !(g.members || []).includes(contact.id)).map(g => (
                        <div key={g.id} onClick={() => {
                          if (onSetGroups) {
                            onSetGroups(prev => prev.map(gr => gr.id === g.id ? { ...gr, members: [...(gr.members || []), contact.id] } : gr));
                          }
                          setAddGroupOpen(false);
                        }} style={{
                          padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, flexShrink: 0 }} />
                          {lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}
                        </div>
                      ))}
                      {groups.filter(g => !(g.members || []).includes(contact.id)).length === 0 && (
                        <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF' }}>
                          {isRtl ? 'عضو في جميع المجموعات' : 'Member of all groups'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* === Notes Tab === */}
          {!isNew && drawerTab === 'notes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Rich text compose area */}
              <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 2, padding: '6px 10px',
                  borderBottom: '1px solid #E5E7EB', background: '#FAFAFA'
                }}>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4, color: '#6B7280', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  ><IconBold size={15} /></button>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4, color: '#6B7280', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  ><IconItalic size={15} /></button>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4, color: '#6B7280', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  ><IconLink size={15} /></button>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4, color: '#6B7280', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  ><IconAt size={15} /></button>
                </div>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder={t.writeNote || (isRtl ? 'اكتب ملاحظة...' : 'Write a note...')}
                  style={{
                    width: '100%', padding: '10px 12px', border: 'none', fontSize: 13,
                    fontFamily: 'inherit', resize: 'vertical', minHeight: 60, maxHeight: 120,
                    outline: 'none', direction: isRtl ? 'rtl' : 'ltr', boxSizing: 'border-box'
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 10px', borderTop: '1px solid #F3F4F6' }}>
                  <button
                    onClick={handleAddNote}
                    disabled={!noteText.trim()}
                    style={{
                      padding: '6px 16px', borderRadius: 6,
                      background: noteText.trim() ? '#5B2D8E' : '#E5E7EB',
                      color: noteText.trim() ? '#fff' : '#9CA3AF',
                      border: 'none', cursor: noteText.trim() ? 'pointer' : 'not-allowed',
                      fontWeight: 600, fontSize: 12, fontFamily: 'inherit'
                    }}
                  >
                    {t.addNote || (isRtl ? 'أضف ملاحظة' : 'Add Note')}
                  </button>
                </div>
              </div>

              {/* Notes list with author info + edit/delete */}
              {notes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {notes.map((note, idx) => {
                    const author = note.author ? { name: note.author, nameAr: note.authorAr || note.author, color: '#5B2D8E' } : NOTE_AUTHORS[idx % NOTE_AUTHORS.length];
                    const isEditing = editingNoteIdx === idx;
                    return (
                      <div key={idx} style={{
                        padding: '12px 14px', borderRadius: 10, background: '#F9FAFB',
                        border: '1px solid #F3F4F6', position: 'relative'
                      }}>
                        {/* Author row */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                          flexDirection: isRtl ? 'row-reverse' : 'row'
                        }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%', background: author.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0
                          }}>
                            {(isRtl ? author.nameAr : author.name).charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', flex: 1 }}>
                            {isRtl ? author.nameAr : author.name}
                          </span>
                          <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                            {isRtl ? note.dateAr : note.dateEn}
                          </span>
                          {/* 3-dot menu */}
                          <div style={{ position: 'relative' }}>
                            <button onClick={() => setNoteMenuIdx(noteMenuIdx === idx ? null : idx)} style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                              color: '#9CA3AF', display: 'flex', alignItems: 'center', borderRadius: 4
                            }}
                              onMouseEnter={e => e.currentTarget.style.background = '#E5E7EB'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              <IconDotsVertical size={14} />
                            </button>
                            {noteMenuIdx === idx && (
                              <div style={{
                                position: 'absolute', top: '100%', [isRtl ? 'left' : 'right']: 0, zIndex: 10,
                                background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 120, marginTop: 2
                              }}>
                                <div onClick={() => {
                                  setEditingNoteIdx(idx);
                                  setEditingNoteText(isRtl ? note.textAr : note.textEn);
                                  setNoteMenuIdx(null);
                                }} style={{
                                  padding: '8px 12px', cursor: 'pointer', fontSize: 12, display: 'flex',
                                  alignItems: 'center', gap: 6, color: '#374151',
                                  flexDirection: isRtl ? 'row-reverse' : 'row'
                                }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                  <IconEdit size={13} />
                                  {t.editNote || (isRtl ? 'تعديل' : 'Edit')}
                                </div>
                                <div onClick={() => { handleDeleteNote(idx); setNoteMenuIdx(null); }} style={{
                                  padding: '8px 12px', cursor: 'pointer', fontSize: 12, display: 'flex',
                                  alignItems: 'center', gap: 6, color: '#EF4444',
                                  flexDirection: isRtl ? 'row-reverse' : 'row'
                                }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                  <IconTrash size={13} />
                                  {t.deleteNote || (isRtl ? 'حذف' : 'Delete')}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Note body */}
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <textarea
                              value={editingNoteText}
                              onChange={e => setEditingNoteText(e.target.value)}
                              style={{
                                width: '100%', padding: '8px 10px', borderRadius: 6,
                                border: '1px solid #D8C5F5', fontSize: 13,
                                fontFamily: 'inherit', resize: 'vertical', minHeight: 40,
                                outline: 'none', direction: isRtl ? 'rtl' : 'ltr', boxSizing: 'border-box'
                              }}
                            />
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button onClick={() => { setEditingNoteIdx(null); setEditingNoteText(''); }} style={{
                                padding: '4px 12px', borderRadius: 6, background: '#F3F4F6',
                                border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', color: '#6B7280'
                              }}>{isRtl ? 'إلغاء' : 'Cancel'}</button>
                              <button onClick={() => handleEditNoteSave(idx)} style={{
                                padding: '4px 12px', borderRadius: 6, background: '#5B2D8E',
                                color: '#fff', border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 600
                              }}>{isRtl ? 'حفظ' : 'Save'}</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5,
                            textAlign: isRtl ? 'right' : 'left' }}>
                            {isRtl ? note.textAr : note.textEn}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#D1D5DB', textAlign: 'center', padding: 20 }}>
                  {isRtl ? 'لا توجد ملاحظات بعد' : 'No notes yet'}
                </div>
              )}
            </div>
          )}

          {/* === Activity Tab === */}
          {!isNew && drawerTab === 'activity' && (() => {
            const ACTIVITY_ICON_MAP = {
              conversation: { Icon: IconMessage, color: '#22C55E', bg: '#F0FDF4' },
              tag: { Icon: IconTag, color: '#D97706', bg: '#FFFBEB' },
              group: { Icon: IconUsers, color: '#1B6B93', bg: '#EBF5FA' },
              broadcast: { Icon: IconSpeakerphone, color: '#5B2D8E', bg: '#F7F2FC' },
              resolved: { Icon: IconCheck, color: '#22C55E', bg: '#F0FDF4' },
            };
            const FILTER_CHIPS = [
              { key: 'all', labelEn: 'All', labelAr: 'الكل' },
              { key: 'groups', labelEn: 'Groups', labelAr: 'المجموعات' },
              { key: 'tags', labelEn: 'Tags', labelAr: 'الوسوم' },
              { key: 'broadcasts', labelEn: 'Broadcasts', labelAr: 'البث' },
              { key: 'conversations', labelEn: 'Conversations', labelAr: 'المحادثات' },
            ];
            const todayEntries = filteredActivity.filter(e => e.dayGroup === 'today');
            const weekEntries = filteredActivity.filter(e => e.dayGroup !== 'today');

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Summary stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { labelEn: 'Conversations', labelAr: 'المحادثات', value: activityStats.conversations, color: '#1B6B93' },
                    { labelEn: 'Customer for', labelAr: 'عميل منذ', value: `${activityStats.tenure}d`, color: '#5B2D8E' },
                    { labelEn: 'Broadcasts', labelAr: 'البث', value: activityStats.broadcasts, color: '#D97706' },
                    { labelEn: 'Groups', labelAr: 'المجموعات', value: activityStats.groups, color: '#22C55E' },
                  ].map((s, i) => (
                    <div key={i} style={{
                      padding: '10px 12px', borderRadius: 8, background: '#F9FAFB',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>
                        {isRtl ? s.labelAr : s.labelEn}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: s.color, fontFamily: 'IBM Plex Mono, monospace', textAlign: isRtl ? 'right' : 'left' }}>
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Filter chips */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {FILTER_CHIPS.map(chip => (
                    <button key={chip.key} onClick={() => setActivityFilter(chip.key)} style={{
                      padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      fontFamily: 'inherit', cursor: 'pointer', border: '1px solid',
                      background: activityFilter === chip.key ? '#5B2D8E' : '#fff',
                      color: activityFilter === chip.key ? '#fff' : '#6B7280',
                      borderColor: activityFilter === chip.key ? '#5B2D8E' : '#E5E7EB',
                    }}>
                      {isRtl ? chip.labelAr : chip.labelEn}
                    </button>
                  ))}
                </div>

                {/* Timeline */}
                {filteredActivity.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#D1D5DB', textAlign: 'center', padding: 20 }}>
                    {t.noActivity || (isRtl ? 'لا يوجد نشاط بعد' : 'No activity yet')}
                  </div>
                ) : (
                  <>
                    {/* TODAY */}
                    {todayEntries.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                          {t.today || (isRtl ? 'اليوم' : 'TODAY')}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          {todayEntries.map((entry, idx) => {
                            const iconDef = ACTIVITY_ICON_MAP[entry.type] || ACTIVITY_ICON_MAP.conversation;
                            return (
                              <div key={entry.id || idx} style={{
                                display: 'flex', gap: 10, padding: '8px 0',
                                flexDirection: isRtl ? 'row-reverse' : 'row'
                              }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: '50%', background: iconDef.bg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                  }}>
                                    <iconDef.Icon size={14} color={iconDef.color} />
                                  </div>
                                  {idx < todayEntries.length - 1 && (
                                    <div style={{ width: 1, flex: 1, background: '#E5E7EB', marginTop: 4 }} />
                                  )}
                                </div>
                                <div style={{ paddingBottom: 6, textAlign: isRtl ? 'right' : 'left', flex: 1 }}>
                                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                                    {isRtl ? entry.actionAr : entry.actionEn}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                                    {isRtl ? entry.dateAr : entry.dateEn}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* THIS WEEK */}
                    {weekEntries.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                          {t.thisWeek || (isRtl ? 'هذا الأسبوع' : 'THIS WEEK')}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          {weekEntries.map((entry, idx) => {
                            const iconDef = ACTIVITY_ICON_MAP[entry.type] || ACTIVITY_ICON_MAP.conversation;
                            return (
                              <div key={entry.id || idx} style={{
                                display: 'flex', gap: 10, padding: '8px 0',
                                flexDirection: isRtl ? 'row-reverse' : 'row'
                              }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: '50%', background: iconDef.bg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                  }}>
                                    <iconDef.Icon size={14} color={iconDef.color} />
                                  </div>
                                  {idx < weekEntries.length - 1 && (
                                    <div style={{ width: 1, flex: 1, background: '#E5E7EB', marginTop: 4 }} />
                                  )}
                                </div>
                                <div style={{ paddingBottom: 6, textAlign: isRtl ? 'right' : 'left', flex: 1 }}>
                                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                                    {isRtl ? entry.actionAr : entry.actionEn}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                                    {isRtl ? entry.dateAr : entry.dateEn}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* === Dashboard Tab === */}
          {!isNew && drawerTab === 'dashboard' && dashStats && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { labelEn: 'Total Messages', labelAr: 'إجمالي الرسائل', value: dashStats.totalMessages, color: '#5B2D8E' },
                  { labelEn: 'Conversations', labelAr: 'المحادثات', value: dashStats.conversations, color: '#1B6B93' },
                  { labelEn: 'Avg Response', labelAr: 'متوسط الرد', value: `${dashStats.avgResponse}h`, color: '#D97706' },
                  { labelEn: 'Resolved', labelAr: 'تم الحل', value: dashStats.resolvedTickets, color: '#22C55E' },
                ].map((stat, i) => (
                  <div key={i} style={{
                    padding: '14px 16px', borderRadius: 10, background: '#F9FAFB',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 600, color: '#9CA3AF',
                      textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
                      textAlign: isRtl ? 'right' : 'left'
                    }}>
                      {isRtl ? stat.labelAr : stat.labelEn}
                    </div>
                    <div style={{
                      fontSize: 22, fontWeight: 800, color: stat.color,
                      fontFamily: 'IBM Plex Mono, monospace',
                      textAlign: isRtl ? 'right' : 'left'
                    }}>
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Satisfaction */}
              <div style={{
                padding: '16px', borderRadius: 10, background: '#F9FAFB',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 10, flexDirection: isRtl ? 'row-reverse' : 'row'
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                    textTransform: 'uppercase', letterSpacing: '0.04em'
                  }}>
                    {isRtl ? 'رضا العميل' : 'SATISFACTION'}
                  </span>
                  <span style={{
                    fontSize: 16, fontWeight: 800,
                    color: dashStats.satisfaction >= 80 ? '#22C55E' : dashStats.satisfaction >= 60 ? '#D97706' : '#EF4444',
                    fontFamily: 'IBM Plex Mono, monospace'
                  }}>
                    {dashStats.satisfaction}%
                  </span>
                </div>
                <div style={{
                  width: '100%', height: 8, borderRadius: 4, background: '#E5E7EB'
                }}>
                  <div style={{
                    width: `${dashStats.satisfaction}%`, height: '100%', borderRadius: 4,
                    background: dashStats.satisfaction >= 80 ? '#22C55E' : dashStats.satisfaction >= 60 ? '#D97706' : '#EF4444',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>

              {/* Last active */}
              <div style={{
                padding: '14px 16px', borderRadius: 10, background: '#F7F2FC',
                border: '1px solid #D8C5F5',
                display: 'flex', alignItems: 'center', gap: 10,
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}>
                <IconClock size={16} color="#5B2D8E" />
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase' }}>
                    {isRtl ? 'آخر نشاط' : 'LAST ACTIVE'}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#5B2D8E' }}>
                    {dashStats.lastActive}
                  </div>
                </div>
              </div>

              {/* Recent activity from history */}
              <div>
                <div style={sectionLabelStyle}>
                  <IconClock size={13} />
                  {isRtl ? 'آخر الأنشطة' : 'RECENT ACTIVITY'}
                </div>
                {history.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {history.slice(0, 5).map((entry, idx) => (
                      <div key={idx} style={{
                        display: 'flex', gap: 12, padding: '8px 0',
                        flexDirection: isRtl ? 'row-reverse' : 'row'
                      }}>
                        <div style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0
                        }}>
                          <div style={{
                            width: 8, height: 8, borderRadius: '50%', background: '#5B2D8E',
                            marginTop: 5
                          }} />
                          {idx < Math.min(history.length, 5) - 1 && (
                            <div style={{ width: 1, flex: 1, background: '#E5E7EB', marginTop: 4 }} />
                          )}
                        </div>
                        <div style={{ paddingBottom: 8, textAlign: isRtl ? 'right' : 'left' }}>
                          <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                            {isRtl ? entry.actionAr : entry.actionEn}
                          </div>
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                            {isRtl ? entry.dateAr : entry.dateEn}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#D1D5DB', textAlign: isRtl ? 'right' : 'left' }}>
                    {isRtl ? 'لا يوجد نشاط بعد' : 'No activity yet'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === Conversations Tab === */}
          {!isNew && drawerTab === 'conversations' && (() => {
            const STATUS_COLORS = { active: '#22C55E', resolved: '#9CA3AF', escalated: '#D97706' };
            const STATUS_LABELS = {
              active: { en: 'Active', ar: 'نشط' },
              resolved: { en: 'Resolved', ar: 'تم الحل' },
              escalated: { en: 'Escalated', ar: 'مُصعّد' },
            };
            const CHANNEL_ICONS = {
              whatsapp: { Icon: IconBrandWhatsapp, color: '#22C55E', label: 'WhatsApp' },
              sms: { Icon: IconMessage, color: '#6366F1', label: 'SMS' },
              email: { Icon: IconMail, color: '#D97706', label: 'Email' },
            };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {demoConversationList.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9CA3AF' }}>
                    <IconBrandWhatsapp size={32} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280' }}>
                      {isRtl ? 'لا توجد محادثات' : 'No conversations'}
                    </div>
                  </div>
                ) : (
                  demoConversationList.map(conv => {
                    const statusColor = STATUS_COLORS[conv.status] || '#9CA3AF';
                    const statusLabel = STATUS_LABELS[conv.status] || STATUS_LABELS.active;
                    const channel = CHANNEL_ICONS[conv.channel] || CHANNEL_ICONS.whatsapp;
                    return (
                      <div key={conv.id} style={{
                        padding: '12px 14px', borderRadius: 10, border: '1px solid #E5E7EB',
                        background: '#fff', cursor: 'pointer'
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        {/* Top row: status dot + subject + time */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                          flexDirection: isRtl ? 'row-reverse' : 'row'
                        }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                            {isRtl ? conv.subjectAr : conv.subjectEn}
                          </div>
                          <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                            {isRtl ? conv.timeAr : conv.timeEn}
                          </span>
                        </div>
                        {/* Preview */}
                        <div style={{
                          fontSize: 12, color: '#6B7280', lineHeight: 1.4, marginBottom: 8,
                          textAlign: isRtl ? 'right' : 'left',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {isRtl ? conv.previewAr : conv.previewEn}
                        </div>
                        {/* Badges row: channel, agent, status */}
                        <div style={{
                          display: 'flex', gap: 6, flexWrap: 'wrap',
                          flexDirection: isRtl ? 'row-reverse' : 'row'
                        }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                            background: '#F3F4F6', color: '#6B7280'
                          }}>
                            <channel.Icon size={11} color={channel.color} />
                            {channel.label}
                          </span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                            background: '#F7F2FC', color: '#5B2D8E'
                          }}>
                            {isRtl ? conv.agent.ar : conv.agent.en}
                          </span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                            background: statusColor + '15', color: statusColor,
                            border: `1px solid ${statusColor}30`
                          }}>
                            {isRtl ? statusLabel.ar : statusLabel.en}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                {/* Load older conversations button */}
                {demoConversationList.length > 0 && (
                  <button
                    onClick={() => {/* Demo: load older */}}
                    style={{
                      width: '100%', padding: '10px 0', marginTop: 8, borderRadius: 8,
                      background: '#F9FAFB', border: '1px solid #E5E7EB', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, color: '#5B2D8E', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F0F8'}
                    onMouseLeave={e => e.currentTarget.style.background = '#F9FAFB'}
                  >
                    <IconClock size={14} />
                    {t.loadOlderConversations || (isRtl ? 'تحميل محادثات أقدم' : 'Load older conversations')}
                  </button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #E5E7EB',
          display: 'flex', gap: 10, justifyContent: 'space-between',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', gap: 10, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button onClick={handleSave} style={{
              padding: '10px 20px', borderRadius: 8, background: '#5B2D8E',
              color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600,
              fontSize: 13, fontFamily: 'inherit'
            }}>
              {t.saveContact || (isRtl ? 'حفظ' : 'Save')}
            </button>
            <button onClick={onClose} style={{
              padding: '10px 16px', borderRadius: 8, background: '#F3F4F6',
              color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600,
              fontSize: 13, fontFamily: 'inherit'
            }}>
              {t.cancel || (isRtl ? 'إلغاء' : 'Cancel')}
            </button>
          </div>
          {!isNew && (
            <button
              onClick={() => { if (window.confirm(t.deleteContactConfirm || 'Delete this contact?')) onDelete(contact.id); }}
              style={{
                padding: '10px 14px', borderRadius: 8, background: '#FEF2F2',
                color: '#EF4444', border: '1px solid #FCA5A5', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
                fontSize: 13, fontWeight: 600
              }}
            >
              <IconTrash size={14} />
              {t.deleteContact || (isRtl ? 'حذف' : 'Delete')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── SegmentModal ──────────────────────────────────────────────────────────────

function SegmentModal({ segment, allTags, contacts, lang, t, onSave, onDelete, onClose, onCreateTag }) {
  const isNew = !segment || segment === 'new';
  const isRtl = lang === 'ar';

  const [nameAr, setNameAr] = useState(isNew ? '' : (segment.nameAr || ''));
  const [nameEn, setNameEn] = useState(isNew ? '' : (segment.nameEn || ''));
  const [tagIds, setTagIds] = useState(isNew ? [] : (segment.tags || []));

  const liveCount = contacts.filter(c =>
    tagIds.length > 0 && tagIds.every(id => c.tags.includes(id))
  ).length;

  function handleSave() {
    const nameArVal = nameAr.trim();
    const nameEnVal = nameEn.trim();
    if ((!nameArVal && !nameEnVal) || tagIds.length === 0) return;
    onSave({
      ...(isNew ? { id: 'seg_' + Date.now(), color: '#5B2D8E', icon: 'ti-users' } : segment),
      nameAr: nameArVal,
      nameEn: nameEnVal,
      tags: tagIds,
      size: liveCount,
    });
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
    fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
  };

  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block'
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300,
          animation: 'fadeIn 0.15s ease'
        }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 440, background: '#fff', borderRadius: 16, zIndex: 301,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        animation: 'slideUp 0.2s ease'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: '#F7F2FC',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <IconUsers size={18} color="#5B2D8E" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 16 }}>
              {isNew
                ? (t.newSegment || (isRtl ? 'شريحة جديدة' : 'New Segment'))
                : (t.editSegment || (isRtl ? 'تعديل الشريحة' : 'Edit Segment'))}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
            padding: 4, display: 'flex', alignItems: 'center'
          }}>
            <IconX size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={{ ...labelStyle, textAlign: isRtl ? 'right' : 'left' }}>
              {isRtl ? 'الاسم بالعربي' : 'Arabic Name'}
            </label>
            <input
              style={{ ...inputStyle, direction: 'rtl', textAlign: 'right' }}
              value={nameAr}
              onChange={e => setNameAr(e.target.value)}
              placeholder="مثال: عملاء VIP"
            />
          </div>
          <div>
            <label style={{ ...labelStyle, textAlign: isRtl ? 'right' : 'left' }}>
              {isRtl ? 'الاسم بالإنجليزي' : 'English Name'}
            </label>
            <input
              style={{ ...inputStyle, direction: 'ltr' }}
              value={nameEn}
              onChange={e => setNameEn(e.target.value)}
              placeholder="e.g. VIP Customers"
            />
          </div>
          <div>
            <label style={{ ...labelStyle, textAlign: isRtl ? 'right' : 'left' }}>
              {t.segmentTags || (isRtl ? 'الوسوم المطلوبة (كل الوسوم يجب أن تنطبق)' : 'Required Tags (all must match)')}
            </label>
            <TagInput
              selectedTagIds={tagIds}
              allTags={allTags}
              onChange={setTagIds}
              onCreateTag={onCreateTag}
              placeholder={t.addTag || (isRtl ? 'أضف وسم...' : 'Add tag...')}
              lang={lang}
            />
          </div>

          {/* Live count */}
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: liveCount > 0 ? '#F0FDF4' : '#F9FAFB',
            border: `1px solid ${liveCount > 0 ? '#BBF7D0' : '#E5E7EB'}`,
            display: 'flex', alignItems: 'center', gap: 10,
            flexDirection: isRtl ? 'row-reverse' : 'row'
          }}>
            <span style={{
              fontSize: 28, fontWeight: 800, color: liveCount > 0 ? '#22C55E' : '#D1D5DB',
              fontFamily: 'IBM Plex Mono, monospace'
            }}>
              {liveCount}
            </span>
            <span style={{ fontSize: 13, color: '#6B7280' }}>
              {isRtl
                ? (tagIds.length === 0 ? 'اختر وسماً لرؤية العملاء المطابقين' : `عميل يملك هذه الوسوم`)
                : (tagIds.length === 0 ? 'Select tags to see matching contacts' : `contacts match`)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #E5E7EB',
          display: 'flex', gap: 10, justifyContent: 'space-between',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', gap: 10, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button
              onClick={handleSave}
              disabled={tagIds.length === 0}
              style={{
                padding: '10px 20px', borderRadius: 8,
                background: tagIds.length === 0 ? '#E5E7EB' : '#5B2D8E',
                color: tagIds.length === 0 ? '#9CA3AF' : '#fff',
                border: 'none', cursor: tagIds.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}
            >
              {t.saveContact || (isRtl ? 'حفظ' : 'Save')}
            </button>
            <button onClick={onClose} style={{
              padding: '10px 16px', borderRadius: 8, background: '#F3F4F6',
              color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600,
              fontSize: 13, fontFamily: 'inherit'
            }}>
              {t.cancel || (isRtl ? 'إلغاء' : 'Cancel')}
            </button>
          </div>
          {!isNew && (
            <button
              onClick={() => { if (window.confirm(t.deleteSegmentConfirm || 'Delete this segment?')) onDelete(segment.id); }}
              style={{
                padding: '10px 14px', borderRadius: 8, background: '#FEF2F2',
                color: '#EF4444', border: '1px solid #FCA5A5', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
                fontSize: 13, fontWeight: 600
              }}
            >
              <IconTrash size={14} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Contacts Component ───────────────────────────────────────────────────

export default function Contacts({ lang, t, industry, contacts: propContacts, setContacts: propSetContacts, tags: propTags, setTags: propSetTags, segments: propSegments, setSegments: propSetSegments, groups: propGroups, setGroups: propSetGroups }) {
  // Support both lifted state from CRM parent and internal state (backward compat)
  const [localContacts, setLocalContacts] = useState([]);
  const [localTags, setLocalTags] = useState([]);
  const [localSegments, setLocalSegments] = useState([]);
  const [localGroups, setLocalGroups] = useState([]);

  const contacts = propContacts || localContacts;
  const setContacts = propSetContacts || setLocalContacts;
  const tags = propTags || localTags;
  const setTags = propSetTags || setLocalTags;
  const segments = propSegments || localSegments;
  const setSegments = propSetSegments || setLocalSegments;
  const groups = propGroups || localGroups;
  const setGroups = propSetGroups || setLocalGroups;

  const [activeSegment, setActiveSegment] = useState(null);
  const [search, setSearch] = useState('');
  const [slideOverId, setSlideOverId] = useState(null);
  const [segmentModal, setSegmentModal] = useState(null);

  // Table enhancements
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filterTag, setFilterTag] = useState(null);
  const [filterGroup, setFilterGroup] = useState(null);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkGroupOpen, setBulkGroupOpen] = useState(false);
  const [bulkRemoveGroupOpen, setBulkRemoveGroupOpen] = useState(false);

  const slideOverContact = slideOverId && slideOverId !== 'new'
    ? contacts.find(c => c.id === slideOverId) : slideOverId;

  useEffect(() => {
    if (!propContacts) setLocalContacts(industry.contacts || []);
    if (!propTags) setLocalTags(industry.tags || []);
    if (!propSegments) setLocalSegments(industry.segments || []);
    if (!propGroups) setLocalGroups(industry.groups || []);
    setActiveSegment(null);
    setSearch('');
    setSelectedIds(new Set());
    setCurrentPage(1);
  }, [industry]);

  const isRtl = lang === 'ar';

  // ── Derived ──────────────────────────────────────────────────────────────────

  const allFiltered = contacts
    .filter(c => {
      if (activeSegment === null) return true;
      const seg = segments.find(s => s.id === activeSegment);
      if (!seg || !Array.isArray(seg.tags) || seg.tags.length === 0) return false;
      return seg.tags.every(tagId => c.tags.includes(tagId));
    })
    .filter(c => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (c.nameEn || '').toLowerCase().includes(q) ||
             (c.nameAr || '').includes(q) ||
             (c.phone || '').includes(q);
    })
    .filter(c => {
      if (filterTag) return (c.tags || []).includes(filterTag);
      return true;
    })
    .filter(c => {
      if (filterGroup) {
        const g = groups.find(gr => gr.id === filterGroup);
        return g ? (g.members || []).includes(c.id) : false;
      }
      return true;
    });

  const sorted = [...allFiltered].sort((a, b) => {
    if (!sortField) return 0;
    let av, bv;
    if (sortField === 'name') {
      av = lang === 'ar' ? (a.nameAr || a.nameEn || '') : (a.nameEn || a.nameAr || '');
      bv = lang === 'ar' ? (b.nameAr || b.nameEn || '') : (b.nameEn || b.nameAr || '');
    } else if (sortField === 'phone') {
      av = a.phone || ''; bv = b.phone || '';
    } else if (sortField === 'tags') {
      av = (a.tags || []).length; bv = (b.tags || []).length;
    } else if (sortField === 'conversations') {
      av = a.conversationCount || 0; bv = b.conversationCount || 0;
    } else if (sortField === 'lastActive') {
      av = a.lastActive ? new Date(a.lastActive).getTime() : 0;
      bv = b.lastActive ? new Date(b.lastActive).getTime() : 0;
    } else { return 0; }
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'desc' ? -cmp : cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const filtered = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleCreateTag(label) {
    const newTag = {
      id: 'tag_' + label.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now(),
      labelEn: label,
      labelAr: label,
      color: AVATAR_COLORS[tags.length % AVATAR_COLORS.length]
    };
    setTags(prev => [...prev, newTag]);
    return newTag;
  }

  function handleSaveContact(contact) {
    setContacts(prev => {
      const idx = prev.findIndex(c => c.id === contact.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = contact;
        return next;
      }
      return [...prev, contact];
    });
    setSlideOverId(null);
  }

  function handleDeleteContact(id) {
    setContacts(prev => prev.filter(c => c.id !== id));
    setSlideOverId(null);
  }

  function handleAddContactNote(contactId, text) {
    setContacts(prev => prev.map(c => {
      if (c.id !== contactId) return c;
      const note = {
        textEn: text, textAr: text,
        dateEn: 'Just now', dateAr: 'الآن'
      };
      return { ...c, notes: [note, ...(c.notes || [])] };
    }));
  }

  function handleUpdateContact(contactId, updates) {
    setContacts(prev => prev.map(c => c.id === contactId ? { ...c, ...updates } : c));
  }

  function handleExport() {
    const header = ['Name (EN)', 'Name (AR)', 'Phone', 'Tags'];
    const rows = contacts.map(c => [
      c.nameEn || '', c.nameAr || '', c.phone || '',
      (c.tags || []).map(tid => {
        const tag = tags.find(t => t.id === tid);
        return tag ? tag.labelEn : tid;
      }).join('; ')
    ]);
    const csvContent = [header, ...rows]
      .map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const fileInputRef = useRef(null);

  function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      // Skip header row
      const newContacts = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = [];
        let current = '';
        let inQuotes = false;
        for (const ch of lines[i]) {
          if (ch === '"') { inQuotes = !inQuotes; }
          else if (ch === ',' && !inQuotes) { cells.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        cells.push(current.trim());
        const [nameEn, nameAr, phone, tagsStr] = cells;
        if (!nameEn && !nameAr) continue;
        const tagNames = (tagsStr || '').split(';').map(s => s.trim()).filter(Boolean);
        const tagIds = tagNames.map(name => {
          const existing = tags.find(t => t.labelEn.toLowerCase() === name.toLowerCase());
          if (existing) return existing.id;
          const newTag = handleCreateTag(name);
          return newTag.id;
        });
        newContacts.push({
          id: 'c_' + Date.now() + '_' + i,
          nameEn: nameEn || '', nameAr: nameAr || '',
          phone: phone || '', tags: tagIds,
        });
      }
      if (newContacts.length > 0) {
        setContacts(prev => [...prev, ...newContacts]);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleSaveSegment(seg) {
    setSegments(prev => {
      const idx = prev.findIndex(s => s.id === seg.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = seg;
        return next;
      }
      return [...prev, seg];
    });
    setSegmentModal(null);
  }

  function handleDeleteSegment(id) {
    setSegments(prev => prev.filter(s => s.id !== id));
    if (activeSegment === id) setActiveSegment(null);
    setSegmentModal(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: 500, animation: 'fadeIn 0.3s ease' }}>

      {/* ── Left Sidebar ─────────────────────────────────────────────────── */}
      <div style={{
        width: 220, flexShrink: 0,
        borderRight: isRtl ? 'none' : '1px solid #E5E7EB',
        borderLeft: isRtl ? '1px solid #E5E7EB' : 'none',
        paddingRight: isRtl ? 0 : 16,
        paddingLeft: isRtl ? 16 : 0,
        display: 'flex', flexDirection: 'column', gap: 4
      }}>
        {/* All contacts */}
        <button
          onClick={() => setActiveSegment(null)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: activeSegment === null ? '#F7F2FC' : 'transparent',
            color: activeSegment === null ? '#5B2D8E' : '#374151',
            fontWeight: activeSegment === null ? 700 : 500,
            fontSize: 13, fontFamily: 'inherit', textAlign: isRtl ? 'right' : 'left',
            width: '100%',
            flexDirection: isRtl ? 'row-reverse' : 'row'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8,
            flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <IconUsers size={15} color={activeSegment === null ? '#5B2D8E' : '#6B7280'} />
            {t.allContacts || (isRtl ? 'جميع جهات الاتصال' : 'All Contacts')}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 700, background: activeSegment === null ? '#5B2D8E' : '#E5E7EB',
            color: activeSegment === null ? '#fff' : '#6B7280',
            borderRadius: 10, padding: '2px 7px', fontFamily: 'IBM Plex Mono, monospace'
          }}>
            {contacts.length}
          </span>
        </button>

        {/* Divider */}
        {segments.length > 0 && (
          <div style={{
            fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em',
            textTransform: 'uppercase', padding: '10px 12px 4px',
            textAlign: isRtl ? 'right' : 'left'
          }}>
            {t.segments || (isRtl ? 'الشرائح' : 'SEGMENTS')}
          </div>
        )}

        {/* Segment list */}
        {segments.map(seg => {
          const count = getSegmentCount(seg, contacts);
          const isActive = activeSegment === seg.id;
          return (
            <div
              key={seg.id}
              style={{ position: 'relative', display: 'flex',
                alignItems: 'center', gap: 4,
                flexDirection: isRtl ? 'row-reverse' : 'row' }}
            >
              <button
                onClick={() => setActiveSegment(isActive ? null : seg.id)}
                style={{
                  flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isActive ? seg.color + '15' : 'transparent',
                  color: isActive ? seg.color : '#374151',
                  fontWeight: isActive ? 700 : 500, fontSize: 13, fontFamily: 'inherit',
                  textAlign: isRtl ? 'right' : 'left',
                  flexDirection: isRtl ? 'row-reverse' : 'row'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8,
                  flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: seg.color, flexShrink: 0
                  }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
                    {lang === 'ar' ? (seg.nameAr || seg.nameEn) : seg.nameEn}
                  </span>
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 700, fontFamily: 'IBM Plex Mono, monospace',
                  background: isActive ? seg.color + '20' : '#E5E7EB',
                  color: isActive ? seg.color : '#6B7280',
                  borderRadius: 10, padding: '2px 7px', flexShrink: 0
                }}>
                  {count}
                </span>
              </button>
              <button
                onClick={() => setSegmentModal(seg)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9CA3AF', padding: '4px', borderRadius: 4,
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                  opacity: 0.5
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
              >
                <IconEdit size={13} />
              </button>
            </div>
          );
        })}

        {/* New segment button */}
        <button
          onClick={() => setSegmentModal('new')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 12px', borderRadius: 8, border: '1px dashed #D1D5DB',
            background: 'transparent', color: '#6B7280', cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit', marginTop: 8, width: '100%',
            flexDirection: isRtl ? 'row-reverse' : 'row'
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#5B2D8E'; e.currentTarget.style.color = '#5B2D8E'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#6B7280'; }}
        >
          <IconFolderPlus size={15} />
          {t.newSegment || (isRtl ? 'شريحة جديدة' : 'New Segment')}
        </button>
      </div>

      {/* ── Main Area ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, paddingLeft: isRtl ? 0 : 24, paddingRight: isRtl ? 24 : 0 }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, flexDirection: isRtl ? 'row-reverse' : 'row', gap: 12
        }}>
          {/* Search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1,
            background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8,
            padding: '8px 12px', flexDirection: isRtl ? 'row-reverse' : 'row'
          }}>
            <IconSearch size={15} color="#9CA3AF" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t.searchContacts || (isRtl ? 'ابحث عن عميل...' : 'Search contacts...')}
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: 13, fontFamily: 'inherit', flex: 1,
                direction: isRtl ? 'rtl' : 'ltr'
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                display: 'flex', alignItems: 'center', padding: 0
              }}>
                <IconX size={14} />
              </button>
            )}
          </div>

          {/* Import / Export / New */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button
              onClick={handleExport}
              title={isRtl ? 'تصدير CSV' : 'Export CSV'}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px',
                borderRadius: 8, background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB',
                cursor: 'pointer', fontWeight: 600, fontSize: 12, fontFamily: 'inherit',
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E5E7EB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; }}
            >
              <IconDownload size={15} />
              {isRtl ? 'تصدير' : 'Export'}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              title={isRtl ? 'استيراد CSV' : 'Import CSV'}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px',
                borderRadius: 8, background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB',
                cursor: 'pointer', fontWeight: 600, fontSize: 12, fontFamily: 'inherit',
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E5E7EB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; }}
            >
              <IconUpload size={15} />
              {isRtl ? 'استيراد' : 'Import'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImport}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => setSlideOverId('new')}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                borderRadius: 8, background: '#5B2D8E', color: '#fff', border: 'none',
                cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}
            >
              <IconPlus size={16} />
              {t.newContact || (isRtl ? 'عميل جديد' : 'New Contact')}
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div style={{
          display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          {/* Tag filter */}
          <select value={filterTag || ''} onChange={e => { setFilterTag(e.target.value || null); setCurrentPage(1); }} style={{
            padding: '5px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12,
            fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
            background: filterTag ? '#F7F2FC' : '#fff', color: filterTag ? '#5B2D8E' : '#374151',
            fontWeight: filterTag ? 600 : 400, direction: isRtl ? 'rtl' : 'ltr'
          }}>
            <option value="">{t.contactTags || (isRtl ? 'كل الوسوم' : 'All Tags')}</option>
            {tags.map(tag => (
              <option key={tag.id} value={tag.id}>{lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}</option>
            ))}
          </select>

          {/* Group filter */}
          {groups.length > 0 && (
            <select value={filterGroup || ''} onChange={e => { setFilterGroup(e.target.value || null); setCurrentPage(1); }} style={{
              padding: '5px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12,
              fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
              background: filterGroup ? '#F7F2FC' : '#fff', color: filterGroup ? '#5B2D8E' : '#374151',
              fontWeight: filterGroup ? 600 : 400, direction: isRtl ? 'rtl' : 'ltr'
            }}>
              <option value="">{t.groupsTab || (isRtl ? 'كل المجموعات' : 'All Groups')}</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}</option>
              ))}
            </select>
          )}

          {(filterTag || filterGroup) && (
            <button onClick={() => { setFilterTag(null); setFilterGroup(null); setCurrentPage(1); }} style={{
              padding: '5px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: '#fff', color: '#EF4444', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit'
            }}>
              {t.clearFilters || (isRtl ? 'مسح' : 'Clear')}
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Save as Smart Group button */}
          {(filterTag || filterGroup || activeSegment) && (
            <button onClick={() => setSegmentModal('new')} style={{
              padding: '5px 12px', borderRadius: 8, border: '1px solid #5B2D8E',
              background: '#F7F2FC', color: '#5B2D8E', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5
            }}>
              <IconWand size={13} />
              {t.saveAsSmartGroup || (isRtl ? 'حفظ كمجموعة ذكية' : 'Save as Smart Group')}
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {(filterTag || filterGroup) && (
          <div style={{
            display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center',
            flexDirection: isRtl ? 'row-reverse' : 'row'
          }}>
            {filterTag && (() => {
              const tag = tags.find(tg => tg.id === filterTag);
              if (!tag) return null;
              const label = lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn;
              return (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                  background: tag.color + '15', color: tag.color, border: `1px solid ${tag.color}30`
                }}>
                  {t.contactTags || (isRtl ? 'وسم' : 'Tag')}: {label}
                  <button onClick={() => { setFilterTag(null); setCurrentPage(1); }} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: tag.color, lineHeight: 1, display: 'flex', alignItems: 'center'
                  }}>
                    <IconX size={13} />
                  </button>
                </span>
              );
            })()}
            {filterGroup && (() => {
              const grp = groups.find(g => g.id === filterGroup);
              if (!grp) return null;
              const label = lang === 'ar' ? (grp.nameAr || grp.nameEn) : grp.nameEn;
              return (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                  background: (grp.color || '#5B2D8E') + '15', color: grp.color || '#5B2D8E',
                  border: `1px solid ${grp.color || '#5B2D8E'}30`
                }}>
                  {t.groupsTab || (isRtl ? 'مجموعة' : 'Group')}: {label}
                  <button onClick={() => { setFilterGroup(null); setCurrentPage(1); }} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: grp.color || '#5B2D8E', lineHeight: 1, display: 'flex', alignItems: 'center'
                  }}>
                    <IconX size={13} />
                  </button>
                </span>
              );
            })()}
            <button onClick={() => setSegmentModal('new')} style={{
              background: 'none', border: 'none', color: '#5B2D8E', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4
            }}>
              {t.saveAsSmartGroup || (isRtl ? 'حفظ كمجموعة ذكية' : 'Save as Smart Group')} →
            </button>
          </div>
        )}

        {/* Count bar */}
        {allFiltered.length > 0 && (
          <div style={{
            fontSize: 12, color: '#9CA3AF', marginBottom: 12,
            textAlign: isRtl ? 'right' : 'left',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexDirection: isRtl ? 'row-reverse' : 'row'
          }}>
            <span>
              <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: 700, color: '#6B7280' }}>
                {allFiltered.length}
              </span>
              {' '}{t.contactsCount || (isRtl ? 'جهة اتصال' : 'contacts')}
            </span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} style={{
              padding: '3px 8px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 11,
              fontFamily: 'inherit', cursor: 'pointer', outline: 'none', color: '#6B7280'
            }}>
              {[10, 20, 50].map(n => (
                <option key={n} value={n}>{n} {t.perPage || (isRtl ? 'لكل صفحة' : 'per page')}</option>
              ))}
            </select>
          </div>
        )}

        {/* Table */}
        {allFiltered.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '60px 20px', color: '#9CA3AF', gap: 12
          }}>
            <IconUser size={40} color="#E5E7EB" />
            <div style={{ fontSize: 15, fontWeight: 600, color: '#6B7280' }}>
              {t.noContacts || (isRtl ? 'لا توجد جهات اتصال' : 'No contacts yet')}
            </div>
            <div style={{ fontSize: 13, textAlign: 'center' }}>
              {t.noContactsDesc || (isRtl ? 'أضف عميلك الأول بالضغط على الزر' : 'Add your first contact using the button above')}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr 140px 1fr 100px 110px',
              padding: '6px 14px', marginBottom: 4, alignItems: 'center'
            }}>
              {/* Select all checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <input type="checkbox"
                  checked={filtered.length > 0 && filtered.every(c => selectedIds.has(c.id))}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedIds(new Set([...selectedIds, ...filtered.map(c => c.id)]));
                    } else {
                      const pageIds = new Set(filtered.map(c => c.id));
                      setSelectedIds(new Set([...selectedIds].filter(id => !pageIds.has(id))));
                    }
                  }}
                  style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#5B2D8E' }}
                />
              </div>
              {/* Sortable headers */}
              {[
                { key: 'name', label: t.contactName || (isRtl ? 'الاسم' : 'Name') },
                { key: 'phone', label: t.contactPhone || (isRtl ? 'الجوال' : 'Phone') },
                { key: 'tags', label: t.contactTags || (isRtl ? 'الوسوم' : 'Tags') },
                { key: 'conversations', label: t.contactConversations || (isRtl ? 'المحادثات' : 'Conversations') },
                { key: 'lastActive', label: t.contactLastActive || (isRtl ? 'آخر نشاط' : 'Last Active') },
              ].map(col => (
                <div key={col.key}
                  onClick={() => {
                    if (sortField === col.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                    else { setSortField(col.key); setSortDir('asc'); }
                  }}
                  style={{
                    fontSize: 11, fontWeight: 700, color: sortField === col.key ? '#5B2D8E' : '#9CA3AF',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    textAlign: isRtl ? 'right' : 'left', cursor: 'pointer', userSelect: 'none',
                    display: 'flex', alignItems: 'center', gap: 4,
                    flexDirection: isRtl ? 'row-reverse' : 'row'
                  }}
                >
                  {col.label}
                  {sortField === col.key && (sortDir === 'asc' ? <IconArrowUp size={11} /> : <IconArrowDown size={11} />)}
                </div>
              ))}
            </div>

            {/* Rows */}
            {filtered.map((contact, idx) => {
              const initials = getInitials(contact.nameEn, contact.nameAr, lang);
              const color = avatarColor(contact.id);
              const contactTags = tags.filter(tg => contact.tags.includes(tg.id));
              const contactSegs = getContactSegments(contact, segments);
              const contactGrps = groups.filter(g => (g.members || []).includes(contact.id));
              const displayName = lang === 'ar' ? (contact.nameAr || contact.nameEn) : (contact.nameEn || contact.nameAr);
              const isSelected = selectedIds.has(contact.id);

              return (
                <div
                  key={contact.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr 140px 1fr 100px 110px',
                    padding: '12px 14px', borderRadius: 10,
                    border: isSelected ? '1px solid #5B2D8E20' : '1px solid transparent',
                    background: isSelected ? '#F7F2FC08' : 'transparent',
                    cursor: 'pointer', transition: 'all 0.15s',
                    animation: `slideUp 0.2s ease ${idx * 0.03}s both`,
                    alignItems: 'center'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = isSelected ? '#F7F2FC' : '#F9FAFB';
                    e.currentTarget.style.border = isSelected ? '1px solid #5B2D8E30' : '1px solid #E5E7EB';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = isSelected ? '#F7F2FC08' : 'transparent';
                    e.currentTarget.style.border = isSelected ? '1px solid #5B2D8E20' : '1px solid transparent';
                  }}
                >
                  {/* Checkbox */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <input type="checkbox" checked={isSelected}
                      onChange={e => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) next.add(contact.id); else next.delete(contact.id);
                        setSelectedIds(next);
                      }}
                      style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#5B2D8E' }}
                    />
                  </div>

                  {/* Name + avatar */}
                  <div onClick={() => setSlideOverId(contact.id)} style={{ display: 'flex', alignItems: 'center', gap: 10,
                    flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', background: color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0
                    }}>
                      {initials}
                    </div>
                    <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E' }}>
                        {displayName}
                      </div>
                      {contactSegs.length > 0 && (
                        <div style={{ fontSize: 11, color: contactSegs[0].color, fontWeight: 500, marginTop: 2 }}>
                          {lang === 'ar' ? (contactSegs[0].nameAr || contactSegs[0].nameEn) : contactSegs[0].nameEn}
                          {contactSegs.length > 1 && ` +${contactSegs.length - 1}`}
                        </div>
                      )}
                      {/* Group badges */}
                      {contactGrps.length > 0 && (
                        <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>
                          {contactGrps.slice(0, 2).map(g => (
                            <span key={g.id} style={{
                              padding: '1px 6px', borderRadius: 8, fontSize: 9, fontWeight: 600,
                              background: g.color + '15', color: g.color
                            }}>
                              {lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}
                            </span>
                          ))}
                          {contactGrps.length > 2 && (
                            <span style={{ fontSize: 9, color: '#9CA3AF' }}>+{contactGrps.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div onClick={() => setSlideOverId(contact.id)} style={{
                    fontSize: 13, color: '#6B7280', fontFamily: 'IBM Plex Mono, monospace',
                    textAlign: isRtl ? 'right' : 'left'
                  }}>
                    {contact.phone || '—'}
                  </div>

                  {/* Tags */}
                  <div onClick={() => setSlideOverId(contact.id)} style={{ display: 'flex', flexWrap: 'wrap', gap: 5,
                    justifyContent: isRtl ? 'flex-end' : 'flex-start' }}>
                    {contactTags.slice(0, 3).map(tag => (
                      <span key={tag.id} style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: tag.color + '15', color: tag.color,
                        border: `1px solid ${tag.color}30`
                      }}>
                        {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
                      </span>
                    ))}
                    {contactTags.length > 3 && (
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, color: '#9CA3AF', background: '#F3F4F6' }}>
                        +{contactTags.length - 3}
                      </span>
                    )}
                    {contactTags.length === 0 && (
                      <span style={{ fontSize: 11, color: '#D1D5DB' }}>—</span>
                    )}
                  </div>

                  {/* Conversations count */}
                  <div onClick={() => setSlideOverId(contact.id)} style={{
                    fontSize: 13, color: '#6B7280', fontFamily: 'IBM Plex Mono, monospace',
                    textAlign: isRtl ? 'right' : 'left'
                  }}>
                    {contact.conversationCount != null ? contact.conversationCount : '—'}
                  </div>

                  {/* Last Active */}
                  <div onClick={() => setSlideOverId(contact.id)} style={{
                    fontSize: 12, color: '#9CA3AF',
                    textAlign: isRtl ? 'right' : 'left'
                  }}>
                    {relativeTime(contact.lastActive, t, isRtl)}
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 16,
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}>
                <button disabled={safePage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  style={{
                    padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                    background: '#fff', cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                    color: safePage <= 1 ? '#D1D5DB' : '#374151', fontSize: 12,
                    display: 'flex', alignItems: 'center'
                  }}>
                  {isRtl ? <IconChevronRight size={14} /> : <IconChevronLeft size={14} />}
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) page = i + 1;
                  else if (safePage <= 3) page = i + 1;
                  else if (safePage >= totalPages - 2) page = totalPages - 4 + i;
                  else page = safePage - 2 + i;
                  return (
                    <button key={page} onClick={() => setCurrentPage(page)}
                      style={{
                        padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: safePage === page ? '1px solid #5B2D8E' : '1px solid #E5E7EB',
                        background: safePage === page ? '#5B2D8E' : '#fff',
                        color: safePage === page ? '#fff' : '#374151',
                        cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', minWidth: 32
                      }}>
                      {page}
                    </button>
                  );
                })}
                <button disabled={safePage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  style={{
                    padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                    background: '#fff', cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                    color: safePage >= totalPages ? '#D1D5DB' : '#374151', fontSize: 12,
                    display: 'flex', alignItems: 'center'
                  }}>
                  {isRtl ? <IconChevronLeft size={14} /> : <IconChevronRight size={14} />}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: '#1A1A2E', color: '#fff', borderRadius: 12, padding: '10px 20px',
            display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            zIndex: 100, fontSize: 13, fontWeight: 600
          }}>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{selectedIds.size}</span>
            <span>{t.selected || (isRtl ? 'محدد' : 'selected')}</span>
            <div style={{ width: 1, height: 20, background: '#374151' }} />

            {/* Assign Tags */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setBulkTagOpen(!bulkTagOpen); setBulkGroupOpen(false); setBulkRemoveGroupOpen(false); }} style={{
                background: '#374151', color: '#fff', border: 'none', borderRadius: 8,
                padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600
              }}>
                {t.assignTags || (isRtl ? 'تعيين وسوم' : 'Assign Tags')}
              </button>
              {bulkTagOpen && (
                <div style={{
                  position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
                  background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 180, maxHeight: 200, overflowY: 'auto'
                }}>
                  {tags.map(tag => (
                    <div key={tag.id} onClick={() => {
                      setContacts(prev => prev.map(c => {
                        if (!selectedIds.has(c.id)) return c;
                        if (c.tags.includes(tag.id)) return c;
                        return { ...c, tags: [...c.tags, tag.id] };
                      }));
                      setBulkTagOpen(false);
                    }} style={{
                      padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151'
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: tag.color }} />
                      {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add to Group */}
            {groups.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => { setBulkGroupOpen(!bulkGroupOpen); setBulkTagOpen(false); setBulkRemoveGroupOpen(false); }} style={{
                  background: '#374151', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600
                }}>
                  {t.addToGroup || (isRtl ? 'أضف لمجموعة' : 'Add to Group')}
                </button>
                {bulkGroupOpen && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
                    background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 180, maxHeight: 200, overflowY: 'auto'
                  }}>
                    {groups.map(g => (
                      <div key={g.id} onClick={() => {
                        setGroups(prev => prev.map(gr => {
                          if (gr.id !== g.id) return gr;
                          const newMembers = new Set(gr.members || []);
                          selectedIds.forEach(id => newMembers.add(id));
                          return { ...gr, members: [...newMembers] };
                        }));
                        setBulkGroupOpen(false);
                      }} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151'
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color }} />
                        {lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Remove from Group */}
            {groups.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => { setBulkRemoveGroupOpen(!bulkRemoveGroupOpen); setBulkTagOpen(false); setBulkGroupOpen(false); }} style={{
                  background: '#374151', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600
                }}>
                  {t.removeFromGroup || (isRtl ? 'إزالة من المجموعة' : 'Remove from Group')}
                </button>
                {bulkRemoveGroupOpen && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
                    background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 180, maxHeight: 200, overflowY: 'auto'
                  }}>
                    {groups.map(g => (
                      <div key={g.id} onClick={() => {
                        setGroups(prev => prev.map(gr => {
                          if (gr.id !== g.id) return gr;
                          const newMembers = (gr.members || []).filter(id => !selectedIds.has(id));
                          return { ...gr, members: newMembers };
                        }));
                        setBulkRemoveGroupOpen(false);
                      }} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151'
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color }} />
                        {lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Delete selected */}
            <button onClick={() => {
              if (window.confirm(isRtl ? `حذف ${selectedIds.size} جهة اتصال؟` : `Delete ${selectedIds.size} contacts?`)) {
                setContacts(prev => prev.filter(c => !selectedIds.has(c.id)));
                setSelectedIds(new Set());
              }
            }} style={{
              background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8,
              padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4
            }}>
              <IconTrash size={13} />
              {t.deleteSelected || (isRtl ? 'حذف' : 'Delete')}
            </button>

            {/* Clear selection */}
            <button onClick={() => setSelectedIds(new Set())} style={{
              background: 'none', color: '#9CA3AF', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', padding: 4
            }}>
              <IconX size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ── Overlays ─────────────────────────────────────────────────────── */}
      {slideOverId !== null && slideOverContact && (
        <ContactSlideOver
          contact={slideOverContact}
          allTags={tags}
          segments={segments}
          contacts={contacts}
          lang={lang}
          t={t}
          onSave={handleSaveContact}
          onDelete={handleDeleteContact}
          onClose={() => setSlideOverId(null)}
          onCreateTag={handleCreateTag}
          onAddNote={handleAddContactNote}
          onUpdateContact={handleUpdateContact}
          groups={groups}
          onSetGroups={setGroups}
          industry={industry}
        />
      )}

      {segmentModal !== null && (
        <SegmentModal
          segment={segmentModal}
          allTags={tags}
          contacts={contacts}
          lang={lang}
          t={t}
          onSave={handleSaveSegment}
          onDelete={handleDeleteSegment}
          onClose={() => setSegmentModal(null)}
          onCreateTag={handleCreateTag}
        />
      )}
    </div>
  );
}

// ── TagsTabView ─────────────────────────────────────────────────────────────

function TagsTabView({ lang, t, tags, setTags, contacts, setContacts, segments, setSegments, groups }) {
  const isRtl = lang === 'ar';
  const [editingTag, setEditingTag] = useState(null);
  const [editLabelEn, setEditLabelEn] = useState('');
  const [editLabelAr, setEditLabelAr] = useState('');
  const [editColor, setEditColor] = useState('#6B7280');
  const [merging, setMerging] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState(null); // tag id or null
  const contextMenuRef = useRef(null);

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    function handleClick(e) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [contextMenu]);

  function startEdit(tag) {
    setEditingTag(tag ? tag.id : 'new');
    setEditLabelEn(tag ? tag.labelEn : '');
    setEditLabelAr(tag ? (tag.labelAr || '') : '');
    setEditColor(tag ? tag.color : PRESET_COLORS[tags.length % PRESET_COLORS.length]);
  }

  function saveTag() {
    if (!editLabelEn.trim() && !editLabelAr.trim()) return;
    if (editingTag === 'new') {
      const newTag = {
        id: 'tag_' + Date.now(),
        labelEn: editLabelEn.trim() || editLabelAr.trim(),
        labelAr: editLabelAr.trim() || editLabelEn.trim(),
        color: editColor
      };
      setTags(prev => [...prev, newTag]);
    } else {
      setTags(prev => prev.map(t => t.id === editingTag ? { ...t, labelEn: editLabelEn.trim(), labelAr: editLabelAr.trim(), color: editColor } : t));
    }
    setEditingTag(null);
  }

  function deleteTag(tagId) {
    setContacts(prev => prev.map(c => ({ ...c, tags: c.tags.filter(tid => tid !== tagId) })));
    setSegments(prev => prev.map(s => ({ ...s, tags: (s.tags || []).filter(tid => tid !== tagId) })));
    setTags(prev => prev.filter(t => t.id !== tagId));
    setDeleteConfirm(null);
  }

  function mergeTags(sourceId, targetId) {
    if (!targetId || sourceId === targetId) return;
    setContacts(prev => prev.map(c => {
      if (!c.tags.includes(sourceId)) return c;
      const newTags = c.tags.filter(tid => tid !== sourceId);
      if (!newTags.includes(targetId)) newTags.push(targetId);
      return { ...c, tags: newTags };
    }));
    setSegments(prev => prev.map(s => {
      const st = s.tags || [];
      if (!st.includes(sourceId)) return s;
      const newSt = st.filter(tid => tid !== sourceId);
      if (!newSt.includes(targetId)) newSt.push(targetId);
      return { ...s, tags: newSt };
    }));
    setTags(prev => prev.filter(t => t.id !== sourceId));
    setMerging(null);
  }

  function createSmartGroupFromTag(tag) {
    const newSeg = {
      id: 'seg_' + Date.now(), type: 'smart',
      nameEn: tag.labelEn, nameAr: tag.labelAr || tag.labelEn,
      color: tag.color, icon: 'ti-wand', tags: [tag.id],
      conditions: { logic: 'AND', rules: [{ field: 'tag', op: 'includes', value: tag.id }] },
      size: contacts.filter(c => (c.tags || []).includes(tag.id)).length
    };
    setSegments(prev => [...prev, newSeg]);
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
    fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
  };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
        flexDirection: isRtl ? 'row-reverse' : 'row'
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E', margin: 0 }}>
          {t.tagsTab || (isRtl ? 'الوسوم' : 'Tags')}
        </h3>
        <button onClick={() => startEdit(null)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          borderRadius: 8, background: '#5B2D8E', color: '#fff', border: 'none',
          cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
        }}>
          <IconPlus size={16} />
          {t.newTag || (isRtl ? 'وسم جديد' : 'New Tag')}
        </button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <IconSearch size={15} color="#9CA3AF" style={{ position: 'absolute', top: 10, [isRtl ? 'right' : 'left']: 12, pointerEvents: 'none' }} />
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t.searchTags || (isRtl ? 'بحث في الوسوم...' : 'Search tags...')}
          style={{
            width: '100%', padding: '8px 12px', [isRtl ? 'paddingRight' : 'paddingLeft']: 36,
            borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit',
            outline: 'none', boxSizing: 'border-box', direction: isRtl ? 'rtl' : 'ltr'
          }}
        />
      </div>

      {/* Tag list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tags.filter(tag => {
          if (!searchQuery.trim()) return true;
          const q = searchQuery.toLowerCase();
          return (tag.labelEn || '').toLowerCase().includes(q) || (tag.labelAr || '').toLowerCase().includes(q);
        }).map(tag => {
          const usage = contacts.filter(c => (c.tags || []).includes(tag.id)).length;
          return (
            <div key={tag.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#fff',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: tag.color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E' }}>
                    {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {lang !== 'ar' && tag.labelAr ? tag.labelAr : tag.labelEn} — {usage} {t.tagUsage || (isRtl ? 'مستخدم' : 'used')}
                  </div>
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <button onClick={(e) => { e.stopPropagation(); setContextMenu(contextMenu === tag.id ? null : tag.id); }} style={{
                  background: contextMenu === tag.id ? '#F3F4F6' : 'transparent', border: 'none', borderRadius: 6, padding: '6px',
                  cursor: 'pointer', display: 'flex', color: '#6B7280'
                }}>
                  <IconDotsVertical size={16} />
                </button>
                {contextMenu === tag.id && (
                  <div ref={contextMenuRef} style={{
                    position: 'absolute', top: '100%', [isRtl ? 'left' : 'right']: 0, marginTop: 4,
                    background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 200,
                    overflow: 'hidden', padding: '4px 0'
                  }}>
                    {[
                      { label: t.renameTag || (isRtl ? 'إعادة تسمية' : 'Rename'), icon: <IconEdit size={14} />, color: '#374151', action: () => { startEdit(tag); setContextMenu(null); } },
                      { label: t.changeColor || (isRtl ? 'تغيير اللون' : 'Change color'), icon: <IconTag size={14} />, color: '#374151', action: () => { startEdit(tag); setContextMenu(null); } },
                      { label: t.mergeWith || (isRtl ? 'دمج مع...' : 'Merge with...'), icon: <IconArrowMerge size={14} />, color: '#374151', action: () => { setMerging(tag.id); setContextMenu(null); } },
                      { label: t.createSmartGroupFromTag || (isRtl ? 'إنشاء مجموعة ذكية من الوسم' : 'Create Smart Group from tag'), icon: <IconWand size={14} />, color: '#5B2D8E', action: () => { createSmartGroupFromTag(tag); setContextMenu(null); } },
                      { type: 'divider' },
                      { label: t.deleteTag || (isRtl ? 'حذف الوسم' : 'Delete'), icon: <IconTrash size={14} />, color: '#EF4444', action: () => { setDeleteConfirm(tag.id); setContextMenu(null); } },
                    ].map((item, idx) => item.type === 'divider' ? (
                      <div key={idx} style={{ height: 1, background: '#E5E7EB', margin: '4px 0' }} />
                    ) : (
                      <div key={idx} onClick={item.action} style={{
                        padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                        fontSize: 13, color: item.color, flexDirection: isRtl ? 'row-reverse' : 'row'
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit/New modal */}
      {editingTag !== null && (
        <>
          <div onClick={() => setEditingTag(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 400, background: '#fff', borderRadius: 16, zIndex: 301,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px'
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>
              {editingTag === 'new' ? (t.newTag || 'New Tag') : (t.editTag || 'Edit Tag')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input style={inputStyle} value={editLabelEn} onChange={e => setEditLabelEn(e.target.value)} placeholder="English label" />
              <input style={{ ...inputStyle, direction: 'rtl' }} value={editLabelAr} onChange={e => setEditLabelAr(e.target.value)} placeholder="الاسم بالعربي" />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t.colorPicker || 'Color'}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setEditColor(c)} style={{
                      width: 24, height: 24, borderRadius: '50%', background: c,
                      border: editColor === c ? '2.5px solid #1A1A2E' : '2.5px solid transparent',
                      cursor: 'pointer', padding: 0
                    }} />
                  ))}
                </div>
              </div>
              {/* Preview */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>Preview:</span>
                <span style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  background: editColor + '18', color: editColor, border: `1px solid ${editColor}40`
                }}>
                  {editLabelEn || editLabelAr || 'Tag'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingTag(null)} style={{
                padding: '8px 16px', borderRadius: 8, background: '#F3F4F6',
                color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.cancel || 'Cancel'}</button>
              <button onClick={saveTag} style={{
                padding: '8px 16px', borderRadius: 8, background: '#5B2D8E',
                color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.saveContact || 'Save'}</button>
            </div>
          </div>
        </>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <>
          <div onClick={() => setDeleteConfirm(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 360, background: '#fff', borderRadius: 16, zIndex: 301,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px', textAlign: 'center'
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A2E', marginBottom: 8 }}>
              {t.confirmDeleteTag || 'Delete this tag? Will affect'} {contacts.filter(c => (c.tags || []).includes(deleteConfirm)).length} {t.contactsCount || 'contacts'}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                padding: '8px 16px', borderRadius: 8, background: '#F3F4F6',
                color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.cancel || 'Cancel'}</button>
              <button onClick={() => deleteTag(deleteConfirm)} style={{
                padding: '8px 16px', borderRadius: 8, background: '#EF4444',
                color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.deleteTag || 'Delete'}</button>
            </div>
          </div>
        </>
      )}

      {/* Merge modal */}
      {merging && (
        <>
          <div onClick={() => setMerging(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 380, background: '#fff', borderRadius: 16, zIndex: 301,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px'
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
              {t.mergeTag || 'Merge Tags'}
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
              {t.mergeInto || 'Merge into'}:
            </div>
            <select value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} style={{
              ...inputStyle, marginBottom: 16
            }}>
              <option value="">-- Select --</option>
              {tags.filter(t => t.id !== merging).map(t => (
                <option key={t.id} value={t.id}>{lang === 'ar' ? (t.labelAr || t.labelEn) : t.labelEn}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setMerging(null)} style={{
                padding: '8px 16px', borderRadius: 8, background: '#F3F4F6',
                color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.cancel || 'Cancel'}</button>
              <button onClick={() => mergeTags(merging, mergeTarget)} disabled={!mergeTarget} style={{
                padding: '8px 16px', borderRadius: 8, background: mergeTarget ? '#5B2D8E' : '#E5E7EB',
                color: mergeTarget ? '#fff' : '#9CA3AF', border: 'none',
                cursor: mergeTarget ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.mergeTag || 'Merge'}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── ConditionBuilderModal ───────────────────────────────────────────────────

function ConditionBuilderModal({ lang, t, tags, groups, contacts, onSave, onClose, initial }) {
  const isRtl = lang === 'ar';
  const [nameEn, setNameEn] = useState(initial?.nameEn || '');
  const [nameAr, setNameAr] = useState(initial?.nameAr || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [color, setColor] = useState(initial?.color || PRESET_COLORS[0]);
  const [logic, setLogic] = useState(initial?.conditions?.logic || 'AND');
  const [rules, setRules] = useState(initial?.conditions?.rules || [{ field: 'tag', op: 'includes', value: '' }]);
  const [showPreviewList, setShowPreviewList] = useState(false);

  // Light theme colors
  const dk = {
    bg: '#FFFFFF', card: '#F9FAFB', border: '#E5E7EB', text: '#1A1A2E',
    muted: '#6B7280', accent: '#22C55E', accentBg: '#22C55E12',
    inputBg: '#F9FAFB', inputBorder: '#E5E7EB',
    pillBg: '#F3F4F6', pillBorder: '#D1D5DB',
  };

  const FIELDS = [
    { key: 'tag', label: isRtl ? 'الوسم' : 'Tag', icon: IconTag, color: '#22C55E' },
    { key: 'lastActive', label: isRtl ? 'آخر نشاط' : 'Last active', icon: IconClock, color: '#3B82F6' },
    { key: 'conversations', label: isRtl ? 'المحادثات' : 'Conversations', icon: IconMessage, color: '#A855F7' },
    { key: 'channel', label: isRtl ? 'القناة' : 'Channel', icon: IconAntenna, color: '#F59E0B' },
    { key: 'group', label: isRtl ? 'المجموعة' : 'Group', icon: IconUsers, color: '#EC4899' },
    { key: 'createdDate', label: isRtl ? 'تاريخ الإنشاء' : 'Created date', icon: IconCalendar, color: '#06B6D4' },
    { key: 'phone', label: isRtl ? 'الجوال' : 'Phone', icon: IconUser, color: '#8B5CF6' },
    { key: 'name', label: isRtl ? 'الاسم' : 'Name', icon: IconUser, color: '#F97316' },
  ];

  const OPS_BY_FIELD = {
    tag: [{ key: 'includes', label: isRtl ? 'يحتوي' : 'contains' }, { key: 'doesNotInclude', label: isRtl ? 'لا يحتوي' : 'does not include' }],
    group: [{ key: 'memberOf', label: isRtl ? 'عضو في' : 'member of' }, { key: 'notMemberOf', label: isRtl ? 'ليس عضو' : 'not member of' }],
    phone: [{ key: 'startsWith', label: isRtl ? 'يبدأ بـ' : 'starts with' }, { key: 'contains', label: isRtl ? 'يحتوي' : 'contains' }],
    name: [{ key: 'contains', label: isRtl ? 'يحتوي' : 'contains' }],
    lastActive: [{ key: 'within', label: isRtl ? 'خلال' : 'within' }, { key: 'moreThan', label: isRtl ? 'أكثر من' : 'more than' }],
    conversations: [{ key: 'moreThan', label: isRtl ? 'أكثر من' : 'more than' }, { key: 'lessThan', label: isRtl ? 'أقل من' : 'less than' }],
    channel: [{ key: 'is', label: isRtl ? 'هو' : 'is' }, { key: 'isNot', label: isRtl ? 'ليس' : 'is not' }],
    createdDate: [{ key: 'within', label: isRtl ? 'خلال' : 'within' }, { key: 'before', label: isRtl ? 'قبل' : 'before' }],
  };

  const TIME_UNITS = [
    { key: 'days', label: isRtl ? 'أيام' : 'days' },
    { key: 'weeks', label: isRtl ? 'أسابيع' : 'weeks' },
    { key: 'months', label: isRtl ? 'أشهر' : 'months' },
  ];

  const CHANNELS = [
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'sms', label: 'SMS' },
    { key: 'email', label: isRtl ? 'بريد إلكتروني' : 'Email' },
  ];

  function updateRule(idx, update) {
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, ...update } : r));
  }

  function removeRule(idx) {
    setRules(prev => prev.filter((_, i) => i !== idx));
  }

  function addConditionOfType(fieldKey) {
    const ops = OPS_BY_FIELD[fieldKey];
    setRules(prev => [...prev, { field: fieldKey, op: ops?.[0]?.key || '', value: '', unit: 'days' }]);
  }

  const matchingContacts = contacts.filter(c => evaluateConditions(c, { logic, rules: rules.filter(r => r.value) }, groups));
  const matchCount = matchingContacts.length;
  const previewContacts = matchingContacts.slice(0, 3);

  function handleSave(draft) {
    if (!nameEn.trim() && !nameAr.trim()) return;
    if (rules.filter(r => r.value).length === 0) return;
    onSave({
      nameEn: nameEn.trim(), nameAr: nameAr.trim(), color, description: description.trim(),
      conditions: { logic, rules: rules.filter(r => r.value) },
      ...(draft ? { draft: true } : {})
    });
  }

  const name = lang === 'ar' ? (nameAr || nameEn || (isRtl ? 'مجموعة ذكية جديدة' : 'New Smart Group')) : (nameEn || nameAr || 'New Smart Group');

  // Pill style for condition selectors
  const pillSelect = {
    padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    border: `1px solid ${dk.pillBorder}`, background: dk.pillBg, color: dk.text,
    cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%238892B0' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: 24,
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 300
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        background: dk.bg, borderRadius: 16, zIndex: 301,
        boxShadow: '0 25px 80px rgba(0,0,0,0.12)', border: `1px solid ${dk.border}`,
        color: dk.text, overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${dk.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: '#22C55E18',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <IconWand size={18} color="#22C55E" />
            </div>
            <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{isRtl ? 'مجموعة ذكية جديدة' : 'New Smart Group'}</div>
              <div style={{ fontSize: 11, color: dk.muted, marginTop: 2 }}>
                {isRtl ? 'الأعضاء يتحدثون تلقائياً عند تغيّر الشروط' : 'Members auto-update when conditions change'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {[IconArrowsMaximize, IconDownload, IconCopy, IconLink, IconTrash].map((Icon, i) => (
              <button key={i} style={{
                width: 32, height: 32, borderRadius: 8, border: `1px solid ${dk.border}`,
                background: '#F3F4F6', color: dk.muted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#E5E7EB'; e.currentTarget.style.color = dk.text; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = dk.muted; }}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {/* Group Name */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: dk.muted, letterSpacing: '0.1em',
              textTransform: 'uppercase', marginBottom: 8
            }}>
              {isRtl ? 'اسم المجموعة' : 'GROUP NAME'}
            </div>
            <div>
              <input
                value={nameEn} onChange={e => { setNameEn(e.target.value); setNameAr(e.target.value); }}
                placeholder={isRtl ? 'اسم المجموعة' : 'Group name'}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${dk.border}`,
                  background: dk.inputBg, color: dk.text, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  boxSizing: 'border-box', direction: isRtl ? 'rtl' : 'ltr'
                }}
              />
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: dk.muted, letterSpacing: '0.1em',
              textTransform: 'uppercase', marginBottom: 8
            }}>
              {isRtl ? 'الوصف' : 'DESCRIPTION'} <span style={{ fontWeight: 400, textTransform: 'lowercase', letterSpacing: 0 }}>({isRtl ? 'اختياري' : 'optional'})</span>
            </div>
            <input
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder={isRtl ? 'وصف المجموعة الذكية...' : 'Describe this smart group...'}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${dk.border}`,
                background: dk.inputBg, color: dk.text, fontSize: 13, fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Conditions Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 12, flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: dk.text }}>
                  {isRtl ? 'الشروط' : 'Conditions'}
                </div>
                <div style={{ fontSize: 11, color: dk.muted, marginTop: 2 }}>
                  {isRtl ? 'العملاء المطابقون ' : 'Customers matching '}
                  <span style={{ fontWeight: 700, color: dk.accent, textDecoration: 'underline' }}>
                    {logic === 'AND' ? (isRtl ? 'كل' : 'all') : (isRtl ? 'أي' : 'any')}
                  </span>
                  {isRtl ? ' هذه الشروط سيتم تضمينهم' : ' of these conditions will be included'}
                </div>
              </div>
              <div style={{
                display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden',
                border: `1px solid ${dk.border}`
              }}>
                {['AND', 'OR'].map(l => (
                  <button key={l} onClick={() => setLogic(l)} style={{
                    padding: '5px 14px', border: 'none', fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                    cursor: 'pointer', letterSpacing: '0.05em',
                    background: logic === l ? dk.accent : dk.card,
                    color: logic === l ? '#0F1629' : dk.muted,
                    transition: 'all 0.15s'
                  }}>
                    {l === 'AND' ? 'ALL' : 'ANY'}
                  </button>
                ))}
              </div>
            </div>

            {/* Condition rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {rules.map((rule, idx) => {
                const fieldDef = FIELDS.find(f => f.key === rule.field) || FIELDS[0];
                const ops = OPS_BY_FIELD[rule.field] || [];
                const isTimeField = rule.field === 'lastActive' || rule.field === 'createdDate';
                const isCountField = rule.field === 'conversations';
                const isChannelField = rule.field === 'channel';
                return (
                  <React.Fragment key={idx}>
                    <div style={{
                      display: 'flex', gap: 8, alignItems: 'center',
                      padding: '12px 16px', borderRadius: 10, background: dk.card, border: `1px solid ${dk.border}`,
                      flexDirection: isRtl ? 'row-reverse' : 'row'
                    }}>
                      {/* Field pill */}
                      <select value={rule.field}
                        onChange={e => updateRule(idx, { field: e.target.value, op: OPS_BY_FIELD[e.target.value]?.[0]?.key || '', value: '', unit: 'days' })}
                        style={{ ...pillSelect, color: fieldDef.color, borderColor: fieldDef.color + '40', background: fieldDef.color + '15' }}>
                        {FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      {/* Op pill */}
                      <select value={rule.op} onChange={e => updateRule(idx, { op: e.target.value })}
                        style={pillSelect}>
                        {ops.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                      {/* Value */}
                      {(rule.field === 'tag') ? (
                        <select value={rule.value} onChange={e => updateRule(idx, { value: e.target.value })}
                          style={{ ...pillSelect, color: dk.accent, borderColor: dk.accent + '40', background: dk.accent + '15' }}>
                          <option value="">--</option>
                          {tags.map(tg => <option key={tg.id} value={tg.id}>{lang === 'ar' ? (tg.labelAr || tg.labelEn) : tg.labelEn}</option>)}
                        </select>
                      ) : (rule.field === 'group') ? (
                        <select value={rule.value} onChange={e => updateRule(idx, { value: e.target.value })}
                          style={{ ...pillSelect, color: '#EC4899', borderColor: '#EC489940', background: '#EC489915' }}>
                          <option value="">--</option>
                          {(groups || []).map(g => <option key={g.id} value={g.id}>{lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}</option>)}
                        </select>
                      ) : isTimeField ? (
                        <>
                          <input value={rule.value || ''} onChange={e => updateRule(idx, { value: e.target.value })}
                            type="number" min="1"
                            style={{
                              ...pillSelect, width: 60, textAlign: 'center', paddingRight: 10,
                              backgroundImage: 'none'
                            }}
                            placeholder="30" />
                          <select value={rule.unit || 'days'} onChange={e => updateRule(idx, { unit: e.target.value })}
                            style={pillSelect}>
                            {TIME_UNITS.map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
                          </select>
                        </>
                      ) : isChannelField ? (
                        <select value={rule.value} onChange={e => updateRule(idx, { value: e.target.value })}
                          style={{ ...pillSelect, color: '#F59E0B', borderColor: '#F59E0B40', background: '#F59E0B15' }}>
                          <option value="">--</option>
                          {CHANNELS.map(ch => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
                        </select>
                      ) : isCountField ? (
                        <input value={rule.value || ''} onChange={e => updateRule(idx, { value: e.target.value })}
                          type="number" min="0"
                          style={{
                            ...pillSelect, width: 60, textAlign: 'center', paddingRight: 10,
                            backgroundImage: 'none'
                          }}
                          placeholder="5" />
                      ) : (
                        <input value={rule.value || ''} onChange={e => updateRule(idx, { value: e.target.value })}
                          style={{
                            padding: '5px 10px', borderRadius: 20, fontSize: 12, fontFamily: 'inherit',
                            border: `1px solid ${dk.pillBorder}`, background: dk.pillBg, color: dk.text,
                            outline: 'none', flex: 1
                          }}
                          placeholder={isRtl ? 'القيمة...' : 'Value...'} />
                      )}
                      <div style={{ flex: 1 }} />
                      <button onClick={() => removeRule(idx)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: dk.muted, display: 'flex',
                        padding: 4, borderRadius: 6
                      }}
                        onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                        onMouseLeave={e => e.currentTarget.style.color = dk.muted}
                      ><IconX size={14} /></button>
                    </div>
                    {/* AND/OR separator between rules */}
                    {idx < rules.length - 1 && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0 6px 20px',
                        flexDirection: isRtl ? 'row-reverse' : 'row'
                      }}>
                        <div style={{
                          fontSize: 10, fontWeight: 700, color: dk.muted, letterSpacing: '0.1em',
                          background: dk.card, padding: '2px 10px', borderRadius: 4, border: `1px solid ${dk.border}`
                        }}>
                          {logic}
                        </div>
                        <div style={{ flex: 1, height: 1, background: dk.border }} />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* + Add condition */}
            <button onClick={() => addConditionOfType('tag')} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0',
              background: 'transparent', border: 'none',
              color: dk.accent, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              marginTop: 12
            }}>
              <IconPlus size={14} />
              {isRtl ? '+ إضافة شرط' : '+ Add condition'}
            </button>
          </div>

          {/* Available Conditions chips */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: dk.muted, letterSpacing: '0.1em',
              textTransform: 'uppercase', marginBottom: 10
            }}>
              {isRtl ? 'الشروط المتاحة' : 'AVAILABLE CONDITIONS'}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {FIELDS.map(f => {
                const Icon = f.icon;
                return (
                  <button key={f.key} onClick={() => addConditionOfType(f.key)} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                    borderRadius: 20, border: `1px solid ${dk.border}`, background: dk.card,
                    color: dk.text, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                    transition: 'all 0.15s'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = f.color; e.currentTarget.style.color = f.color; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = dk.border; e.currentTarget.style.color = dk.text; }}
                  >
                    <Icon size={13} color={f.color} />
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Live Preview */}
          <div style={{
            borderRadius: 12, border: `1px solid ${dk.border}`, background: dk.card, overflow: 'hidden'
          }}>
            <div style={{
              padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: matchCount > 0 ? dk.accent : dk.muted,
                  boxShadow: matchCount > 0 ? `0 0 8px ${dk.accent}80` : 'none'
                }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: dk.accent }}>
                  {isRtl ? 'معاينة مباشرة' : 'Live Preview'}
                </span>
                <span style={{ fontSize: 11, color: dk.muted }}>
                  {isRtl ? 'العملاء المطابقون الآن' : 'Matching customers right now'}
                </span>
              </div>
              <span style={{
                fontSize: 18, fontWeight: 800, color: matchCount > 0 ? dk.accent : dk.muted,
                fontFamily: 'IBM Plex Mono, monospace'
              }}>
                {matchCount}
              </span>
            </div>

            {/* Preview customer rows */}
            {previewContacts.length > 0 && (
              <div style={{ borderTop: `1px solid ${dk.border}` }}>
                {previewContacts.map((c, i) => {
                  const initials = getInitials(c.nameEn, c.nameAr, lang);
                  const bgColor = avatarColor(c.id);
                  const displayName = lang === 'ar' ? (c.nameAr || c.nameEn) : (c.nameEn || c.nameAr);
                  const timeLabels = ['3 min ago', '1 hour ago', '3 hours ago'];
                  const timeLabelsAr = ['قبل ٣ دقائق', 'قبل ساعة', 'قبل ٣ ساعات'];
                  return (
                    <div key={c.id} style={{
                      padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
                      borderTop: i > 0 ? `1px solid ${dk.border}` : 'none',
                      flexDirection: isRtl ? 'row-reverse' : 'row'
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: bgColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0
                      }}>
                        {initials}
                      </div>
                      <div style={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: dk.text }}>
                          {displayName}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: dk.muted }}>
                        {isRtl ? (timeLabelsAr[i] || timeLabelsAr[0]) : (timeLabels[i] || timeLabels[0])}
                      </span>
                    </div>
                  );
                })}
                {matchCount > 3 && (
                  <div style={{
                    padding: '10px 18px', textAlign: 'center', borderTop: `1px solid ${dk.border}`
                  }}>
                    <button onClick={() => setShowPreviewList(!showPreviewList)} style={{
                      background: 'none', border: 'none', color: dk.accent, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
                    }}>
                      {isRtl ? `عرض كل ${matchCount} عميل مطابق →` : `View all ${matchCount} matching customers →`}
                    </button>
                  </div>
                )}
              </div>
            )}
            {previewContacts.length === 0 && (
              <div style={{
                padding: '20px 18px', textAlign: 'center', borderTop: `1px solid ${dk.border}`,
                color: dk.muted, fontSize: 13
              }}>
                {isRtl ? 'أضف شروط لرؤية العملاء المطابقين' : 'Add conditions to see matching customers'}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${dk.border}`,
          display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <button onClick={onClose} style={{
            padding: '10px 16px', borderRadius: 8, background: 'transparent',
            color: dk.muted, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
          }}>{isRtl ? 'إلغاء' : 'Cancel'}</button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => handleSave(true)} style={{
              padding: '10px 18px', borderRadius: 8, background: dk.card,
              color: dk.text, border: `1px solid ${dk.border}`, cursor: 'pointer',
              fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
            }}>
              {isRtl ? 'حفظ كمسودة' : 'Save as Draft'}
            </button>
            <button onClick={() => handleSave(false)} style={{
              padding: '10px 20px', borderRadius: 8, background: dk.accent,
              color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
              boxShadow: `0 2px 12px ${dk.accent}40`
            }}>
              {isRtl ? 'إنشاء مجموعة ذكية' : 'Create Smart Group'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── GroupsView ───────────────────────────────────────────────────────────────

function GroupsView({ lang, t, industry, groups, setGroups, contacts, setContacts, tags, segments, setSegments }) {
  const isRtl = lang === 'ar';
  const [view, setView] = useState('grid'); // 'grid' | 'detail'
  const [detailGroupId, setDetailGroupId] = useState(null);
  const [showModal, setShowModal] = useState(null); // null | 'static' | 'smart'
  const [editGroup, setEditGroup] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all' | 'static' | 'smart'
  const [contextMenu, setContextMenu] = useState(null); // group id or null

  // Static group modal state
  const [modalNameEn, setModalNameEn] = useState('');
  const [modalNameAr, setModalNameAr] = useState('');
  const [modalColor, setModalColor] = useState(PRESET_COLORS[0]);
  const [modalDesc, setModalDesc] = useState('');

  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const detailGroup = groups.find(g => g.id === detailGroupId);
  const allGroups = [
    ...groups.map(g => ({ ...g, _type: 'static' })),
    ...(segments || []).filter(s => s.type === 'smart').map(s => ({ ...s, _type: 'smart', members: contacts.filter(c => evaluateConditions(c, s.conditions, groups)).map(c => c.id) })),
  ];

  const filteredGroups = filterType === 'all' ? allGroups
    : filterType === 'static' ? allGroups.filter(g => g._type === 'static')
    : allGroups.filter(g => g._type === 'smart');

  function deleteSmartGroup(id) {
    setSegments(prev => prev.filter(s => s.id !== id));
    setDeleteConfirm(null);
  }

  function openStaticModal(group) {
    setEditGroup(group || null);
    setModalNameEn(group ? group.nameEn : '');
    setModalNameAr(group ? (group.nameAr || '') : '');
    setModalColor(group ? group.color : PRESET_COLORS[groups.length % PRESET_COLORS.length]);
    setModalDesc(group ? (group.description || '') : '');
    setShowModal('static');
    setShowTypeMenu(false);
  }

  function saveStaticGroup() {
    if (!modalNameEn.trim() && !modalNameAr.trim()) return;
    if (editGroup) {
      setGroups(prev => prev.map(g => g.id === editGroup.id ? { ...g, nameEn: modalNameEn.trim(), nameAr: modalNameAr.trim(), color: modalColor, description: modalDesc } : g));
    } else {
      const newGroup = {
        id: 'grp_' + Date.now(),
        nameEn: modalNameEn.trim() || modalNameAr.trim(),
        nameAr: modalNameAr.trim() || modalNameEn.trim(),
        color: modalColor, members: [], description: modalDesc
      };
      setGroups(prev => [...prev, newGroup]);
    }
    setShowModal(null);
  }

  function deleteGroup(id) {
    setGroups(prev => prev.filter(g => g.id !== id));
    setDeleteConfirm(null);
    if (detailGroupId === id) { setView('grid'); setDetailGroupId(null); }
  }

  function removeMember(groupId, contactId) {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, members: (g.members || []).filter(m => m !== contactId) } : g));
  }

  function addMember(groupId, contactId) {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, members: [...new Set([...(g.members || []), contactId])] } : g));
    setAddMemberOpen(false);
  }

  function handleSaveSmartGroup(data) {
    const newSeg = {
      id: 'seg_' + Date.now(), type: 'smart',
      nameEn: data.nameEn, nameAr: data.nameAr, color: data.color,
      icon: 'ti-wand', tags: [],
      conditions: data.conditions,
      size: contacts.filter(c => evaluateConditions(c, data.conditions, groups)).length
    };
    setSegments(prev => [...prev, newSeg]);
    setShowModal(null);
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
    fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
  };

  // Detail view
  if (view === 'detail' && detailGroup) {
    const memberContacts = contacts.filter(c => (detailGroup.members || []).includes(c.id));
    return (
      <div style={{ animation: 'fadeIn 0.3s ease' }}>
        <button onClick={() => { setView('grid'); setDetailGroupId(null); }} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
          background: 'transparent', border: '1px solid #E5E7EB', color: '#6B7280',
          cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', marginBottom: 16,
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          {isRtl ? <IconChevronRight size={14} /> : <IconChevronLeft size={14} />}
          {t.backToGroups || (isRtl ? 'رجوع' : 'Back')}
        </button>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <span style={{ width: 14, height: 14, borderRadius: '50%', background: detailGroup.color }} />
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1A1A2E' }}>
              {lang === 'ar' ? (detailGroup.nameAr || detailGroup.nameEn) : detailGroup.nameEn}
            </h3>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>
              {memberContacts.length} {t.groupMembers || 'Members'}
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setAddMemberOpen(!addMemberOpen)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
              background: '#5B2D8E', color: '#fff', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
            }}>
              <IconPlus size={16} />
              {t.addMembers || (isRtl ? 'إضافة أعضاء' : 'Add Members')}
            </button>
            {addMemberOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: isRtl ? 'auto' : 0, left: isRtl ? 0 : 'auto',
                marginTop: 4, background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 240, maxHeight: 260, overflowY: 'auto', zIndex: 10
              }}>
                {contacts.filter(c => !(detailGroup.members || []).includes(c.id)).map(c => (
                  <div key={c.id} onClick={() => addMember(detailGroup.id, c.id)} style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', background: avatarColor(c.id),
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700
                    }}>
                      {getInitials(c.nameEn, c.nameAr, lang)}
                    </div>
                    {lang === 'ar' ? (c.nameAr || c.nameEn) : (c.nameEn || c.nameAr)}
                  </div>
                ))}
                {contacts.filter(c => !(detailGroup.members || []).includes(c.id)).length === 0 && (
                  <div style={{ padding: '12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                    {isRtl ? 'جميع العملاء أعضاء' : 'All contacts are members'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Member table */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {memberContacts.map(c => (
            <div key={c.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: avatarColor(c.id),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700
                }}>
                  {getInitials(c.nameEn, c.nameAr, lang)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {lang === 'ar' ? (c.nameAr || c.nameEn) : (c.nameEn || c.nameAr)}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'IBM Plex Mono, monospace' }}>{c.phone || '—'}</div>
                </div>
              </div>
              <button onClick={() => removeMember(detailGroup.id, c.id)} style={{
                background: '#FEF2F2', border: 'none', borderRadius: 6, padding: '6px',
                cursor: 'pointer', display: 'flex', color: '#EF4444'
              }}><IconX size={14} /></button>
            </div>
          ))}
          {memberContacts.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9CA3AF' }}>
              <IconUsers size={32} color="#D1D5DB" />
              <div style={{ fontSize: 13, marginTop: 8 }}>{isRtl ? 'لا يوجد أعضاء' : 'No members'}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
        flexDirection: isRtl ? 'row-reverse' : 'row'
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E', margin: 0 }}>
          {t.groupsTab || (isRtl ? 'المجموعات' : 'Groups')}
        </h3>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowTypeMenu(!showTypeMenu)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
            borderRadius: 8, background: '#5B2D8E', color: '#fff', border: 'none',
            cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
          }}>
            <IconPlus size={16} />
            {t.newGroup || (isRtl ? 'مجموعة جديدة' : 'New Group')}
          </button>
          {showTypeMenu && (
            <div style={{
              position: 'absolute', top: '100%', right: isRtl ? 'auto' : 0, left: isRtl ? 0 : 'auto',
              marginTop: 4, background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 180, zIndex: 10
            }}>
              <div onClick={() => openStaticModal(null)} style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151',
                display: 'flex', alignItems: 'center', gap: 8
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <IconUsers size={15} color="#6B7280" />
                {t.staticGroup || (isRtl ? 'مجموعة ثابتة' : 'Static Group')}
              </div>
              <div onClick={() => { setShowModal('smart'); setShowTypeMenu(false); }} style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151',
                display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F3F4F6'
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <IconWand size={15} color="#5B2D8E" />
                {t.smartGroup || (isRtl ? 'مجموعة ذكية' : 'Smart Group')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
        <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>{isRtl ? 'عرض:' : 'Show:'}</span>
        {['all', 'static', 'smart'].map(f => (
          <button key={f} onClick={() => setFilterType(f)} style={{
            padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            border: filterType === f ? '1px solid #5B2D8E' : '1px solid #E5E7EB',
            background: filterType === f ? '#F7F2FC' : '#fff',
            color: filterType === f ? '#5B2D8E' : '#6B7280',
            cursor: 'pointer', transition: 'all 0.15s'
          }}>
            {f === 'all' ? (t.filterAll || 'All') : f === 'static' ? (t.filterStatic || 'Static') : (t.filterSmart || 'Smart')}
          </button>
        ))}
      </div>

      {/* Group cards */}
      {filteredGroups.length === 0 ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '60px 20px', color: '#9CA3AF', gap: 12
        }}>
          <IconUsers size={40} color="#E5E7EB" />
          <div style={{ fontSize: 15, fontWeight: 600, color: '#6B7280' }}>
            {t.noGroups || (isRtl ? 'لا توجد مجموعات بعد' : 'No groups yet')}
          </div>
          <div style={{ fontSize: 13 }}>{t.noGroupsDesc || (isRtl ? 'أنشئ مجموعتك الأولى' : 'Create your first group')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {filteredGroups.map((g, gi) => {
            const memberCount = (g.members || []).length;
            const isSmart = g._type === 'smart';
            const memberIds = (g.members || []).slice(0, 4);
            const memberContacts = memberIds.map(mid => contacts.find(c => c.id === mid)).filter(Boolean);
            const overflow = memberCount - memberContacts.length;
            const accentColor = isSmart ? '#22D3EE' : g.color;
            // Conditions preview for smart groups
            const conditionsPreview = isSmart && g.conditions && g.conditions.rules
              ? g.conditions.rules.map(r => `${r.field || ''} ${r.operator || ''} ${r.value || ''}`).join(` ${(g.conditions.logic || 'AND').toUpperCase()} `)
              : '';
            return (
              <div key={g.id} onClick={() => {
                if (!isSmart) { setDetailGroupId(g.id); setView('detail'); }
              }} style={{
                padding: '16px 20px', borderRadius: 12,
                border: isSmart ? '1px solid rgba(34,211,238,0.3)' : '1px solid #E5E7EB', background: '#fff',
                borderLeft: !isRtl ? `4px solid ${accentColor}` : undefined,
                borderRight: isRtl ? `4px solid ${accentColor}` : undefined,
                cursor: isSmart ? 'default' : 'pointer', transition: 'all 0.15s',
                position: 'relative'
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* Top row: name + badge + 3-dot menu */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  flexDirection: isRtl ? 'row-reverse' : 'row'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                      {isSmart && <IconBolt size={15} color="#22D3EE" />}
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1A1A2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lang === 'ar' ? (g.nameAr || g.nameEn) : g.nameEn}
                      </div>
                    </div>
                    {g.description && (
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.description}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {isSmart ? (
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: 'rgba(34,211,238,0.1)', color: '#22D3EE',
                        border: '1px solid rgba(34,211,238,0.3)', display: 'flex', alignItems: 'center', gap: 3
                      }}>
                        <IconBolt size={10} /> SMART
                      </span>
                    ) : (
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: '#F0FDF4', color: '#22C55E', border: '1px solid #BBF7D0'
                      }}>
                        {t.staticGroup || 'Static'}
                      </span>
                    )}
                    {/* 3-dot context menu */}
                    <div style={{ position: 'relative' }}>
                      <button onClick={e => { e.stopPropagation(); setContextMenu(contextMenu === g.id ? null : g.id); }} style={{
                        background: 'transparent', border: 'none', borderRadius: 6, padding: '3px',
                        cursor: 'pointer', display: 'flex', color: '#9CA3AF'
                      }}><IconDotsVertical size={15} /></button>
                      {contextMenu === g.id && (
                        <div style={{
                          position: 'absolute', top: '100%', right: isRtl ? 'auto' : 0, left: isRtl ? 0 : 'auto',
                          marginTop: 4, background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 150, zIndex: 20
                        }}>
                          {isSmart ? (
                            <>
                              <div onClick={e => { e.stopPropagation(); setContextMenu(null); }} style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151',
                                display: 'flex', alignItems: 'center', gap: 8
                              }}
                                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              >
                                <IconEdit size={13} color="#6B7280" />
                                {t.editConditions || 'Edit conditions'}
                              </div>
                              <div onClick={e => { e.stopPropagation(); setContextMenu(null); setDeleteConfirm(g.id); }} style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#EF4444',
                                display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F3F4F6'
                              }}
                                onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              >
                                <IconTrash size={13} color="#EF4444" />
                                {t.deleteGroup || 'Delete'}
                              </div>
                            </>
                          ) : (
                            <>
                              <div onClick={e => { e.stopPropagation(); setContextMenu(null); openStaticModal(g); }} style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151',
                                display: 'flex', alignItems: 'center', gap: 8
                              }}
                                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              >
                                <IconEdit size={13} color="#6B7280" />
                                {t.rename || 'Rename'}
                              </div>
                              <div onClick={e => { e.stopPropagation(); setContextMenu(null); setDeleteConfirm(g.id); }} style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#EF4444',
                                display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F3F4F6'
                              }}
                                onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              >
                                <IconTrash size={13} color="#EF4444" />
                                {t.deleteGroup || 'Delete'}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Smart group conditions preview */}
                {isSmart && conditionsPreview && (
                  <div style={{ fontSize: 11, color: '#22D3EE', marginTop: 6, fontFamily: 'IBM Plex Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {conditionsPreview}
                  </div>
                )}

                {/* Member avatar stack */}
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <div style={{ display: 'flex', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    {memberContacts.map((c, i) => (
                      <div key={c.id} style={{
                        width: 28, height: 28, borderRadius: '50%', background: avatarColor(c.id),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 10, fontWeight: 700, border: '2px solid #fff',
                        marginLeft: !isRtl && i > 0 ? -8 : 0,
                        marginRight: isRtl && i > 0 ? -8 : 0,
                        zIndex: memberContacts.length - i, position: 'relative'
                      }}>
                        {getInitials(c.nameEn, c.nameAr, lang)}
                      </div>
                    ))}
                    {overflow > 0 && (
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', background: '#E5E7EB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#6B7280', fontSize: 10, fontWeight: 700, border: '2px solid #fff',
                        marginLeft: !isRtl ? -8 : 0,
                        marginRight: isRtl ? -8 : 0,
                        zIndex: 0, position: 'relative'
                      }}>
                        +{overflow}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: !isRtl ? 8 : 0, marginRight: isRtl ? 8 : 0 }}>
                    {memberCount} {t.groupMembers || 'Members'}{isSmart ? ` (${t.autoUpdated || 'auto-updated'})` : ''}
                  </span>
                </div>

                {/* Bottom row: group type label + broadcast timestamp */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10,
                  flexDirection: isRtl ? 'row-reverse' : 'row'
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: isSmart ? '#22D3EE' : '#9CA3AF' }}>
                    {isSmart ? (isRtl ? 'مجموعة ذكية' : 'SMART GROUP') : (isRtl ? 'مجموعة ثابتة' : 'STATIC GROUP')}
                  </span>
                  <span style={{ fontSize: 10, color: '#9CA3AF' }}>
                    {gi === 0
                      ? `${t.lastBroadcast || 'Last broadcast'}: 3d ago`
                      : isSmart
                        ? (isRtl ? 'تحديث: الآن' : 'Updated: just now')
                        : (t.noBroadcastYet || 'No broadcasts yet')
                    }
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Static group modal */}
      {showModal === 'static' && (
        <>
          <div onClick={() => setShowModal(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 420, background: '#fff', borderRadius: 16, zIndex: 301,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px'
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>
              {editGroup ? (t.editGroup || 'Edit Group') : (t.newGroup || 'New Group')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input style={inputStyle} value={modalNameEn} onChange={e => setModalNameEn(e.target.value)} placeholder="English name" />
              <input style={{ ...inputStyle, direction: 'rtl' }} value={modalNameAr} onChange={e => setModalNameAr(e.target.value)} placeholder="الاسم بالعربي" />
              <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={modalDesc} onChange={e => setModalDesc(e.target.value)}
                placeholder={isRtl ? 'الوصف (اختياري)' : 'Description (optional)'} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => setModalColor(c)} style={{
                    width: 24, height: 24, borderRadius: '50%', background: c,
                    border: modalColor === c ? '2.5px solid #1A1A2E' : '2.5px solid transparent',
                    cursor: 'pointer', padding: 0
                  }} />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(null)} style={{
                padding: '8px 16px', borderRadius: 8, background: '#F3F4F6',
                color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.cancel || 'Cancel'}</button>
              <button onClick={saveStaticGroup} style={{
                padding: '8px 16px', borderRadius: 8, background: '#5B2D8E',
                color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.saveContact || 'Save'}</button>
            </div>
          </div>
        </>
      )}

      {/* Smart group modal (condition builder) */}
      {showModal === 'smart' && (
        <ConditionBuilderModal
          lang={lang} t={t} tags={tags} groups={groups} contacts={contacts}
          onSave={handleSaveSmartGroup} onClose={() => setShowModal(null)}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <>
          <div onClick={() => setDeleteConfirm(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 360, background: '#fff', borderRadius: 16, zIndex: 301,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '24px', textAlign: 'center'
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A2E', marginBottom: 16 }}>
              {t.deleteGroup || (isRtl ? 'حذف المجموعة؟' : 'Delete this group?')}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                padding: '8px 16px', borderRadius: 8, background: '#F3F4F6',
                color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.cancel || 'Cancel'}</button>
              <button onClick={() => {
                const isSmartDel = (segments || []).some(s => s.id === deleteConfirm);
                if (isSmartDel) deleteSmartGroup(deleteConfirm);
                else deleteGroup(deleteConfirm);
              }} style={{
                padding: '8px 16px', borderRadius: 8, background: '#EF4444',
                color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit'
              }}>{t.deleteGroup || 'Delete'}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { TagsTabView as Tags, GroupsView as Groups };
